import React, { useEffect, useMemo, useState, Suspense } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  SignOut,
  Plus,
  EnvelopeSimple,
  ListChecks,
  ChatCircleText,
  Gear,
  CheckCircle,
  XCircle,
  Clock,
  Funnel,
  CaretDown,
  PaperPlaneTilt,
  Eye,
  Trash,
  DownloadSimple,
  MagnifyingGlass,
  Truck,
  ArrowSquareOut,
  PlugsConnected,
  Broadcast,
  UserPlus,
  Users,
  Circle,
  Receipt,
  ArrowsClockwise,
  CopySimple,
  LinkSimple,
  Phone,
  MapTrifold,
  CalendarCheck,
  Handshake,
  VideoCamera,
  UploadSimple as Upload,
  Buildings,
  ChartBar as BarChart,
  ShieldCheck,
  Megaphone,
  Compass,
  UserCircle,
  Rocket,
  Bell,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { apiClient, BACKEND_URL, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import TripMap from "../components/TripMap";
import RecurringSeries from "../components/RecurringSeries";
import { autoPrefixUS } from "../components/PhoneInput";
// Legacy components code-split to shave ~120 KB from the initial admin bundle.
// These panels are hidden behind the "Legacy (archive)" sidebar group.
const DispatchAlertSettingsPanel = React.lazy(() => import("./admin/DispatchAlertSettingsPanel"));
const DispatchConsole = React.lazy(() => import("../components/DispatchConsole"));
// Marketing Video Engine (Sprint M1) — lazy since it's a new tab and only power users open it.
const MarketingPanel = React.lazy(() => import("./admin/MarketingPanel"));
// KaizenPanel is the portal scanner UI (offers list, run-once, credential status,
// portal Terms). Paired with KaizenEmailTriggerPanel — portal scan is the reliable
// 5-min base loop; email trigger is the sub-60s fast lane. BOTH surface here.
const KaizenPanel = React.lazy(() => import("./admin/KaizenPanel"));
const AccountingPanel = React.lazy(() => import("./admin/accounting/AccountingPanel"));
const AdminUsersPanel = React.lazy(() => import("./admin/AdminUsersPanel"));
const CommPolicyPanel = React.lazy(() => import("./admin/CommPolicyPanel"));
const MainLinePanel = React.lazy(() => import("./admin/MainLinePanel"));
const DispatchGuidePanel = React.lazy(() => import("./admin/DispatchGuidePanel"));
const VAMonitoringPanel = React.lazy(() => import("./admin/VAMonitoringPanel"));
const ZonesPanel = React.lazy(() => import("./admin/ZonesPanel"));
import MeetingsPanel from "../components/MeetingsPanel";
import AdminFacilities from "./admin/AdminFacilities";
import ImportTrips from "./admin/ImportTrips";
// KaizenPanel is lazy-loaded above (primary nav — paired with KaizenEmailTriggerPanel)
import TPTripsPanel from "./admin/TPTripsPanel";
import ProvidersPanel from "./admin/ProvidersPanel";
import TemplatesPanel from "./admin/TemplatesPanel";
import TripArchivePanel from "./admin/TripArchivePanel";
import ManageTripsPanel from "./admin/ManageTripsPanel";
import ScorecardsPanel from "./admin/ScorecardsPanel";
import CredentialingQueuePanel from "./admin/CredentialingQueuePanel";
import CredentialingSubmissionsPanel from "./admin/CredentialingSubmissionsPanel";
import CredentialingCoverage from "./admin/CredentialingCoverage";
import CredentialingIncidentsModal from "./CredentialingIncidentsModal";
import InvoicingPanel from "./admin/InvoicingPanel";
import ReconciliationPanel from "./admin/ReconciliationPanel";
import ActivityLogPanel from "./admin/ActivityLogPanel";
import AStatAuthPanel from "./admin/AStatAuthPanel";
import MyProfilePanel from "./admin/MyProfilePanel";
import BrokerConfirmationsPanel from "./admin/BrokerConfirmationsPanel";
import KaizenEmailTriggerPanel from "./admin/KaizenEmailTriggerPanel";

/* -------- Global admin search --------
 * Bible · Admin feeling: "I can see everything." One box, anything findable.
 * Debounced 220ms. Backend caps 20 results. Kbd shortcut: focused input,
 * ↑/↓ navigate results, Enter opens.
 */
function GlobalSearch({ onNavigate, onOpenTrip }) {
  const [q, setQ] = React.useState("");
  const [results, setResults] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [idx, setIdx] = React.useState(0);
  const inputRef = React.useRef(null);
  const boxRef = React.useRef(null);

  // Debounced query
  React.useEffect(() => {
    const query = q.trim();
    if (query.length < 2) { setResults([]); return; }
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { apiClient } = await import("../lib/api");
        const r = await apiClient.get(`/admin/search`, { params: { q: query, limit: 12 } });
        const body = r.data?.data || r.data || {};
        setResults(body.results || []);
        setIdx(0);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 220);
    return () => clearTimeout(timer);
  }, [q]);

  // Click-outside dismiss + global ⌘K / Ctrl+K focus shortcut (Bible §Dispatcher
  // efficiency — power users open search with one keystroke).
  React.useEffect(() => {
    function onDoc(e) {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    }
    function onKeyDown(e) {
      const isMod = e.metaKey || e.ctrlKey;
      if (isMod && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setOpen(true);
        if (inputRef.current) inputRef.current.focus();
      }
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  const pick = (r) => {
    setOpen(false);
    setQ("");
    if (!r) return;
    // Trip result → scroll+highlight in Live Ops. Everything else → tab jump.
    if (r.kind === "trip" && r.meta?.trip_id && onOpenTrip) {
      onOpenTrip(r.meta.trip_id);
      return;
    }
    if (r.route && onNavigate) onNavigate(r.route);
  };

  const onKey = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setIdx(i => Math.min(results.length - 1, i + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setIdx(i => Math.max(0, i - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); pick(results[idx]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  const kindTone = (k) => ({
    trip:   "bg-blue-50 text-blue-700",
    rider:  "bg-forest/10 text-forest",
    driver: "bg-amber-50 text-amber-700",
    tp:     "bg-purple-50 text-purple-700",
    org:    "bg-slate-100 text-slate-600",
  }[k] || "bg-slate-100 text-slate-600");

  return (
    <div ref={boxRef} className="relative">
      <input
        ref={inputRef}
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKey}
        placeholder="Search trips, riders, drivers, TPs…  (⌘K)"
        data-testid="admin-global-search"
        className="hidden md:block w-72 rounded-md border border-[#E2E0D8] bg-white px-3 py-1.5 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-forest/30"
      />
      {open && q.trim().length >= 2 && (
        <div
          data-testid="admin-global-search-results"
          className="absolute right-0 top-full mt-1 w-96 bg-white border border-[#E2E0D8] rounded-lg shadow-lg z-50 max-h-[420px] overflow-y-auto"
        >
          {loading && <div className="p-3 text-xs text-slate-400">Searching…</div>}
          {!loading && results.length === 0 && (
            <div className="p-3 text-xs text-slate-400" data-testid="admin-search-empty">Nothing matched &quot;{q}&quot;.</div>
          )}
          {results.map((r, i) => (
            <button
              key={`${r.kind}::${r.id}`}
              onClick={() => pick(r)}
              onMouseEnter={() => setIdx(i)}
              data-testid={`admin-search-result-${r.kind}-${r.id}`}
              className={"w-full text-left px-3 py-2 border-b border-[#E2E0D8] last:border-b-0 hover:bg-slate-50 " + (idx === i ? "bg-slate-50" : "")}
            >
              <div className="flex items-center gap-2">
                <span className={"text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full " + kindTone(r.kind)}>
                  {r.kind}
                </span>
                <div className="text-sm font-semibold text-slate_navy truncate flex-1">{r.title}</div>
              </div>
              <div className="text-xs text-slate-500 mt-0.5 truncate">{r.subtitle}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
import NetworkCoveragePanel from "./admin/NetworkCoveragePanel";
// --- New consolidated panels (Waves 1-3, see /app/memory/ROADMAP.md) ---
import HomePanel from "./admin/HomePanel";
import TrustOpsPanel from "./admin/TrustOpsPanel";
import TrustScoreBadge from "../components/TrustScoreBadge";
import VaAlertOverlay from "../components/VaAlertOverlay";
import Softphone from "../components/softphone/Softphone";
const BrokersKaizenPanel = React.lazy(() => import("./admin/BrokersKaizenPanel"));
const CPDPanel = React.lazy(() => import("./admin/CPDPanel"));
import OutreachPanel from "./admin/OutreachPanel";
import SourcesPanel from "./admin/SourcesPanel";
import FleetPanel from "./admin/FleetPanel";
import CredentialingHubPanel from "./admin/CredentialingHubPanel";
import TestHarnessPanel from "./floor/TestHarnessPanel";
import { CheckCircle2 } from "lucide-react";
import { XCircle as XCircleLucide } from "lucide-react";
import { useAppPref } from "../lib/appPrefs";

const STATUS_STYLES = {
  pending: "bg-terracotta/10 text-terracotta border-terracotta/20",
  confirmed: "bg-forest/10 text-forest border-forest/20",
  completed: "bg-slate-100 text-slate-600 border-slate-200",
  cancelled: "bg-red-50 text-red-700 border-red-200",
};

const EVENT_LABEL = {
  "booking.created": "Request received",
  "trip.forwarded": "Dispatched to OVA",
  "trip.forward_failed": "Dispatch failed",
  "trip.polled": "Polled OVA",
  "trip.webhook": "OVA update",
  "trip.driver_assigned": "Driver assigned",
  "trip.driver_arrived": "Driver at pickup",
  "trip.in_progress": "Trip in progress",
  "trip.completed": "Trip completed",
  "trip.cancelled": "Trip cancelled",
  "booking.status.pending": "Marked pending",
  "booking.status.confirmed": "Marked confirmed",
  "booking.status.completed": "Marked completed",
  "booking.status.cancelled": "Marked cancelled",
};

const NAV = [
  // --- New consolidated primary nav (see /app/memory/ARCHITECTURE.md §Sidebar) ---
  { id: "floor", label: "The Floor", icon: Compass },
  { id: "dispatch-guide", label: "Dispatch Guide", icon: Rocket },
  { id: "dispatch-alerts", label: "Dispatch Alerts", icon: Bell },
  { id: "my-profile", label: "My Profile", icon: UserCircle },
  { id: "trust-ops", label: "Trust Ops", icon: ShieldCheck },
  { id: "manage-trips", label: "Manage Trips", icon: ListChecks },
  { id: "test-harness", label: "Nova Test Harness", icon: Rocket },
  { id: "home", label: "Home", icon: Broadcast },
  { id: "live-ops", label: "Live Ops", icon: MapTrifold },
  { id: "fleet", label: "Fleet", icon: Handshake },
  { id: "credentialing-hub", label: "Credentialing", icon: ShieldCheck },
  { id: "zones", label: "Zones & Regions", icon: MapTrifold },
  { id: "outreach", label: "Outreach", icon: ChatCircleText },
  { id: "sources", label: "Sources", icon: PlugsConnected },
  // Broker integrations (Kaizen is first; new brokers added here as needed)
  { id: "brokers-kaizen", label: "Kaizen", icon: PlugsConnected },
  { id: "cpd-program", label: "CPD Program", icon: ShieldCheck },
  { id: "monitoring", label: "VA Monitoring", icon: VideoCamera },
  { id: "astat-auth", label: "MedexOVA Auth", icon: ShieldCheck },
  { id: "marketing", label: "Marketing", icon: Megaphone },
  { id: "finance", label: "Finance", icon: Receipt },
  { id: "accounting", label: "Accounting Agent", icon: Receipt },
  { id: "activity", label: "Activity", icon: ListChecks },
  { id: "broker-conf", label: "Broker POD-back", icon: Receipt },
  { id: "comms", label: "Comms", icon: ChatCircleText },
  { id: "settings-hub", label: "Settings", icon: Gear },
  { id: "admin-users", label: "Admin Users", icon: Users },
  { id: "comm-policy", label: "Comm Policy", icon: ChatCircleText },
  { id: "main-line", label: "Main Line", icon: Phone },  // --- Legacy IDs kept for backwards-compat + Legacy sidebar group (archived, not deleted) ---
  { id: "dispatch", label: "Dispatch (legacy)", icon: Broadcast },
  { id: "tp-trips", label: "TP Trips (legacy)", icon: MapTrifold },
  { id: "providers", label: "Providers (legacy)", icon: Handshake },
  { id: "network-coverage", label: "Network Coverage (legacy)", icon: MapTrifold },
  { id: "tp-recurring", label: "TP Recurring (legacy)", icon: CalendarCheck },
  { id: "credentialing", label: "Credentialing Overview (legacy)", icon: Users },
  { id: "credentialing-submissions", label: "Submissions (legacy)", icon: ShieldCheck },
  { id: "credentialing-feed", label: "Credentialing Feed (legacy)", icon: ShieldCheck },
  { id: "credentialing-coverage", label: "Field Coverage (legacy)", icon: ShieldCheck },
  { id: "completed", label: "Completed", icon: ListChecks },
  { id: "cancelled", label: "Cancelled", icon: ListChecks },
  { id: "scorecards", label: "TP Scorecards", icon: BarChart },
  { id: "invoicing", label: "TP Invoicing", icon: Receipt },
  { id: "bookings", label: "Bookings", icon: ListChecks },
  { id: "import", label: "Import", icon: Upload },
  { id: "kaizen", label: "Kaizen", icon: PlugsConnected },
  { id: "facilities", label: "Facilities", icon: Buildings },
  { id: "recurring", label: "Recurring", icon: CalendarCheck },
  { id: "meetings", label: "Meetings", icon: VideoCamera },
  { id: "drivers", label: "Drivers (legacy)", icon: Users },
  { id: "partners", label: "Partners (legacy)", icon: Handshake },
  { id: "messages", label: "Messages", icon: ChatCircleText },
  { id: "ova", label: "OVA Dispatch", icon: Truck },
  { id: "delivery", label: "Delivery Log", icon: Receipt },
  { id: "settings", label: "Email Settings", icon: Gear },
  { id: "integrations", label: "Integrations", icon: Gear },
  { id: "preferences", label: "Preferences", icon: Gear },
];

// New primary sidebar structure. Legacy items are archived behind the "Legacy"
// group (hidden by default via preferences.showLegacyGroup).
// See /app/memory/ARCHITECTURE.md §Sidebar and /app/memory/DUPLICATE_INVENTORY.md
const NAV_GROUPS = [
  { id: "grp-primary", label: "MedexOVA", icon: Broadcast, items: ["floor", "dispatch-guide", "dispatch-alerts", "my-profile", "trust-ops", "manage-trips", "test-harness", "home", "live-ops", "fleet", "credentialing-hub", "zones", "outreach", "sources", "astat-auth", "marketing", "monitoring"] },
  { id: "grp-brokers", label: "Brokers", icon: PlugsConnected, items: ["brokers-kaizen", "cpd-program"] },
  { id: "grp-money",   label: "Finance & Comms", icon: Receipt, items: ["accounting", "finance", "activity", "broker-conf", "comms"] },
  { id: "grp-cfg",     label: "Settings", icon: Gear, items: ["settings-hub", "admin-users", "comm-policy", "main-line"] },
  { id: "grp-legacy",  label: "Legacy (archive)", icon: ListChecks, items: [
    "dispatch", "tp-trips", "providers", "network-coverage", "tp-recurring",
    "credentialing", "credentialing-submissions", "credentialing-feed", "credentialing-coverage",
    "completed", "cancelled", "scorecards", "invoicing", "bookings",
    "import", "facilities", "recurring", "meetings",
    "drivers", "partners", "messages", "ova", "delivery",
    "settings", "integrations", "preferences",
    // Kept as deep-link routes; the primary NAV surfaces them via brokers-kaizen.
    "kaizen", "kaizen-email",
  ]},
];

function GroupedNav({ groups, items, activeId, onSelect, hrefFor }) {
  const byId = React.useMemo(() => Object.fromEntries(items.map(i => [i.id, i])), [items]);
  const initialOpen = () => {
    const open = {};
    for (const g of groups) open[g.id] = g.items.includes(activeId);
    return open;
  };
  const [open, setOpen] = React.useState(initialOpen);
  const [hoverGroup, setHoverGroup] = React.useState(null);
  React.useEffect(() => {
    setOpen((prev) => {
      const next = { ...prev };
      for (const g of groups) if (g.items.includes(activeId)) next[g.id] = true;
      return next;
    });
  }, [activeId, groups]);
  const toggle = (gid) => setOpen(s => ({ ...s, [gid]: !s[gid] }));
  return (
    <nav className="flex lg:flex-col gap-1.5" data-testid="grouped-nav">
      {groups.map(({ id, label, icon: Icon, items: itemIds }) => {
        const isOpen = !!open[id];
        const isActiveGroup = itemIds.includes(activeId);
        return (
          <div
            key={id}
            className="w-full relative"
            onMouseEnter={() => setHoverGroup(id)}
            onMouseLeave={() => setHoverGroup((v) => (v === id ? null : v))}
          >
            <button
              onClick={() => toggle(id)}
              data-testid={`nav-group-${id}`}
              className="group w-full inline-flex items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold overflow-hidden relative"
              style={{
                color: isActiveGroup ? "#0F3D2E" : "#0F172A",
                background: isActiveGroup ? "rgba(15,61,46,0.06)" : "transparent",
                border: `1px solid ${isActiveGroup ? "rgba(15,61,46,0.14)" : "transparent"}`,
                transition: "background 220ms cubic-bezier(0.22,1,0.36,1), border-color 220ms",
              }}
            >
              {/* transport slide-underlay on hover */}
              <span
                aria-hidden
                className="absolute inset-y-0 left-0 pointer-events-none"
                style={{
                  width: hoverGroup === id && !isActiveGroup ? "100%" : "0%",
                  background: "linear-gradient(90deg, rgba(15,61,46,0.06), transparent)",
                  transition: "width 320ms cubic-bezier(0.22,1,0.36,1)",
                }}
              />
              <span className="inline-flex items-center gap-2.5 relative z-10">
                <span
                  className="inline-flex items-center justify-center rounded-md"
                  style={{
                    width: 22, height: 22,
                    background: isActiveGroup ? "#0F3D2E" : "rgba(15,23,42,0.04)",
                    color: isActiveGroup ? "#fff" : "#0F172A",
                    transition: "transform 220ms cubic-bezier(0.34,1.56,0.64,1)",
                    transform: hoverGroup === id ? "scale(1.08)" : "scale(1)",
                  }}
                >
                  <Icon size={13} weight={isActiveGroup ? "fill" : "bold"} />
                </span>
                {label}
              </span>
              <motion.span
                animate={{ rotate: isOpen ? 90 : 0 }}
                transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                className="text-xs relative z-10"
                style={{ color: "#94A3B8" }}
              >
                ▸
              </motion.span>
            </button>
            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                  className="overflow-hidden ml-3 pl-3 border-l border-[#E2E0D8] my-1"
                >
                  <div className="flex flex-col gap-0.5 py-1">
                    {itemIds.map((iid, subIdx) => {
                      const it = byId[iid];
                      if (!it) return null;
                      const ItemIcon = it.icon;
                      const isActive = activeId === iid;
                      // Real <a href> (via router Link) so users can right-click ->
                      // "Open in new tab" / cmd-click, not just a JS click handler.
                      // credentialing-feed opens a modal, not a route — stays a plain button.
                      const isModalItem = iid === "credentialing-feed";
                      const Comp = isModalItem ? motion.button : motion(Link);
                      const navProps = isModalItem
                        ? { onClick: () => onSelect(iid) }
                        : { to: hrefFor ? hrefFor(iid) : `/admin/${iid}` };
                      return (
                        <Comp
                          key={iid}
                          initial={{ opacity: 0, x: -6 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.22, delay: subIdx * 0.03, ease: [0.22, 1, 0.36, 1] }}
                          whileHover={{ x: 3 }}
                          {...navProps}
                          data-testid={`admin-tab-${iid}`}
                          className="inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm text-left relative overflow-hidden"
                          style={{
                            background: isActive ? "#0F3D2E" : "transparent",
                            color: isActive ? "#fff" : "#0F172A",
                            border: `1px solid ${isActive ? "#0F3D2E" : "transparent"}`,
                            transition: "background 200ms, border-color 200ms",
                            fontWeight: isActive ? 700 : 500,
                            textDecoration: "none",
                          }}
                          onMouseEnter={(e) => {
                            if (!isActive) e.currentTarget.style.background = "rgba(15,61,46,0.05)";
                          }}
                          onMouseLeave={(e) => {
                            if (!isActive) e.currentTarget.style.background = "transparent";
                          }}
                        >
                          {isActive && (
                            <motion.span
                              layoutId="nav-active-marker"
                              className="absolute left-0 top-1 bottom-1 w-1 rounded-r"
                              style={{ background: "#F59E0B" }}
                            />
                          )}
                          <ItemIcon size={13} weight={isActive ? "fill" : "regular"} />
                          {it.label}
                        </Comp>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </nav>
  );
}

// FirstRunTour — 4-step tooltip walkthrough for a new dispatcher/VA. Uses
// localStorage so it fires exactly once per browser. Zero deps, zero routes,
// zero new files (per Experience Polish Sprint mandate). Bible §Grandma Test
// for dispatchers: "resolves the situation on their first shift without
// training documentation."
function FirstRunTour({ role }) {
  const STORAGE_KEY = "astat.first_run_tour.done.v1";
  const [step, setStep] = React.useState(() => {
    if (typeof window === "undefined") return -1;
    try { return window.localStorage.getItem(STORAGE_KEY) === "1" ? -1 : 0; } catch { return -1; }
  });
  const steps = React.useMemo(() => ([
    {
      title: "This is Home",
      body: "Everything that needs your attention lives here — active SOS, pending applications, and escalations you can resolve in three taps.",
    },
    {
      title: "Search anything, instantly",
      body: "Press ⌘K (or Ctrl-K) from any screen to search across trips, riders, drivers, and providers. Enter jumps you straight to the record.",
    },
    {
      title: "Resolve escalations inline",
      body: "Each escalation shows the reason on the same line. Tap Suggest driver → tap a candidate → you're done. No hunting.",
    },
    {
      title: `You're ${role === "va" ? "a VA" : "a dispatcher"} — the sidebar is scoped to what you need`,
      body: "Legacy screens are hidden by default. If you ever need to look up pre-migration data, an admin can toggle Legacy on in Settings.",
    },
  ]), [role]);
  const dismiss = () => {
    try { window.localStorage.setItem(STORAGE_KEY, "1"); } catch { /* */ }
    setStep(-1);
  };
  if (step < 0 || step >= steps.length) return null;
  const cur = steps[step];
  const isLast = step === steps.length - 1;
  return (
    <div
      className="fixed inset-0 z-[9998] flex items-end sm:items-center justify-center px-4 pb-4"
      style={{ background: "rgba(15, 23, 42, 0.35)" }}
      data-testid="admin-first-run-tour"
      onClick={(e) => { if (e.currentTarget === e.target) dismiss(); }}
    >
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl p-5 space-y-3" data-testid={`admin-first-run-step-${step}`}>
        <div className="flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-widest text-forest font-bold">
            Welcome · {step + 1} of {steps.length}
          </div>
          <button
            type="button"
            onClick={dismiss}
            data-testid="admin-first-run-skip"
            className="text-xs text-slate-500 hover:text-slate-800"
          >Skip tour</button>
        </div>
        <div className="text-lg font-heading font-bold text-slate_navy">{cur.title}</div>
        <div className="text-sm text-slate-600">{cur.body}</div>
        <div className="flex items-center gap-2 pt-1">
          <div className="flex-1 flex items-center gap-1">
            {steps.map((_, i) => (
              <span key={i} className="h-1.5 rounded-full transition-all"
                style={{ background: i <= step ? "#2B4C3B" : "#E2E0D8", width: i === step ? 24 : 8 }}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={() => isLast ? dismiss() : setStep(step + 1)}
            data-testid={isLast ? "admin-first-run-finish" : "admin-first-run-next"}
            className="px-4 py-2 rounded-full bg-forest text-white text-sm font-bold hover:bg-forest-hover"
          >
            {isLast ? "Got it" : "Next"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const { section } = useParams();
  const [incidentsOpen, setIncidentsOpen] = useState(false);
  // credentialing-feed is a modal, not a routable tab — exclude from validIds
  const validIds = useMemo(() => NAV.map((n) => n.id).filter(id => id !== "credentialing-feed"), []);
  const tab = section && validIds.includes(section) ? section : "trust-ops";
  const [showLegacy] = useAppPref("showLegacyGroup");
  // VA-scoped view: role === "va" gets a curated sidebar (home / applications / outreach / comms only).
  // See /app/memory/ROLES.md §5 and /app/memory/ROADMAP.md §Stream H.
  const isVA = (user?.role || "").toLowerCase() === "va";
  const visibleGroups = useMemo(() => {
    let groups = NAV_GROUPS;
    if (!showLegacy) groups = groups.filter(g => g.id !== "grp-legacy");
    if (isVA) {
      // VAs see only Home, Credentialing (New Applications tab), Outreach (+ Comms). No legacy, no settings, no finance.
      const allowed = new Set(["floor", "trust-ops", "home", "credentialing-hub", "outreach", "comms", "brokers-kaizen", "accounting"]);
      groups = groups
        .map(g => ({ ...g, items: g.items.filter(i => allowed.has(i)) }))
        .filter(g => g.items.length > 0);
    }
    return groups;
  }, [showLegacy, isVA]);
  // Redirect bare /admin → /admin/trust-ops so URLs are always deep-linkable.
  // Also redirect the retired standalone Applications screen to its new home
  // (Credentialing → New Applications tab) so old bookmarks/links still work.
  useEffect(() => {
    if (!section) { nav("/admin/trust-ops", { replace: true }); return; }
    if (section === "applications") nav("/admin/credentialing-hub", { replace: true });
  }, [section, nav]);
  const setTab = (id) => {
    if (id === "credentialing-feed") { setIncidentsOpen(true); return; }
    if (id === "floor") { nav("/floor"); return; }
    nav(`/admin/${id}`);
  };

  // Global-search deep-link — when the user picks a trip from search, stash the
  // trip_id so Live Ops preselects + scrolls to it on mount. One-shot: cleared
  // by the child once consumed.
  const [pendingTripFocus, setPendingTripFocus] = useState(null);
  const openTripInLiveOps = (tripId) => {
    setPendingTripFocus(tripId);
    setTab("live-ops");
  };

  const onLogout = () => {
    logout();
    nav("/admin/login");
  };

  return (
    <main className="min-h-screen bg-bone">
      <FirstRunTour role={user?.role} />
      <VaAlertOverlay />
      <Softphone />
      {/* top bar */}
      <div className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-black/5">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-6 h-14 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2" data-testid="admin-brand-link">
            <span className="relative h-8 w-8 rounded-md bg-transparent inline-flex items-center justify-center overflow-hidden">
              <img src="/logo.png" alt="MedexOVA" className="h-7 w-7 object-contain logo-glow" />
            </span>
            <span className="font-heading text-base tracking-tight text-slate_navy">
              <span className="text-[#16A34A]">Medex</span><span className="text-ova">OVA</span> <span className="text-slate-400 font-normal">/ admin</span>
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <GlobalSearch onNavigate={setTab} onOpenTrip={openTripInLiveOps} />
            <TrustScoreBadge onClick={() => setTab("trust-ops")} />
            <span className="hidden sm:block text-xs text-slate-500">
              Signed in as <span className="font-medium text-slate_navy">{user?.email}</span>
            </span>
            <button
              onClick={onLogout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 rounded-md border border-[#E2E0D8] bg-white hover:bg-slate-50 text-slate_navy px-3 py-1.5 text-sm font-medium"
            >
              <SignOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 lg:px-6 py-8 grid lg:grid-cols-[220px_1fr] gap-8">
        {/* sidebar — grouped, collapsible */}
        <aside className="lg:sticky lg:top-20 h-fit">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-brandblue mb-3">Operations</div>
          <GroupedNav
            groups={visibleGroups}
            items={NAV}
            activeId={tab}
            onSelect={setTab}
            hrefFor={(id) => (id === "floor" ? "/floor" : `/admin/${id}`)}
          />
        </aside>

        <div>
          <Suspense fallback={<div className="text-center py-16 text-slate-500">Loading panel…</div>}>
          {tab === "trust-ops" && <TrustOpsPanel onNavigate={setTab} onOpenTrip={openTripInLiveOps} />}
          {tab === "test-harness" && <TestHarnessPanel />}
          {tab === "dispatch-guide" && <DispatchGuidePanel />}
          {tab === "dispatch-alerts" && <Suspense fallback={<div>Loading…</div>}><DispatchAlertSettingsPanel /></Suspense>}
          {tab === "home" && <HomePanel onNavigate={setTab} />}
          {tab === "brokers-kaizen" && <BrokersKaizenPanel />}
          {tab === "cpd-program" && <CPDPanel />}
          {tab === "live-ops" && <TPTripsPanel initialTripId={pendingTripFocus} />}
          {tab === "fleet" && <FleetPanel renderDrivers={() => <DriversPanel />} />}
          {tab === "credentialing-hub" && <CredentialingHubPanel />}
          {tab === "outreach" && <OutreachPanel />}
          {tab === "sources" && <SourcesPanel onNavigate={setTab} />}
          {tab === "kaizen-email" && <KaizenEmailTriggerPanel />}
          {tab === "astat-auth" && <AStatAuthPanel />}
          {tab === "my-profile" && <MyProfilePanel />}
          {tab === "marketing" && (
            <Suspense fallback={<div className="p-8 text-sm text-slate-500">Loading marketing engine…</div>}>
              <MarketingPanel />
            </Suspense>
          )}
          {tab === "finance" && (
            <div data-testid="finance-panel">
              <ReconciliationPanel />
              <InvoicingPanel />
            </div>
          )}
          {tab === "accounting" && <AccountingPanel isVA={isVA} />}
          {tab === "activity" && <ActivityLogPanel />}
          {tab === "broker-conf" && <BrokerConfirmationsPanel />}
          {tab === "comms" && <DeliveryLogPanel />}
          {tab === "settings-hub" && <IntegrationsPanel />}
          {tab === "zones" && <ZonesPanel />}
          {tab === "dispatch" && <DispatchConsole />}
          {tab === "tp-trips" && <TPTripsPanel />}
          {tab === "providers" && <ProvidersPanel />}
          {tab === "network-coverage" && <NetworkCoveragePanel />}
          {tab === "tp-recurring" && <TemplatesPanel />}
          {tab === "credentialing" && <CredentialingQueuePanel />}
          {tab === "credentialing-submissions" && <CredentialingSubmissionsPanel />}
          {tab === "credentialing-coverage" && <CredentialingCoverage />}
          {tab === "manage-trips" && <ManageTripsPanel />}
          {tab === "completed" && (
            <TripArchivePanel
              status={["COMPLETED"]}
              title="Completed trips"
              subtitle="All trips that finished with a signed POD, grouped by date."
              testidPrefix="completed"
              icon={CheckCircle2}
            />
          )}
          {tab === "cancelled" && (
            <TripArchivePanel
              status={["CANCELLED", "NO_SHOW_CONFIRMED"]}
              title="Cancelled & no-shows"
              subtitle="Trips that were cancelled or ended in a confirmed no-show, grouped by date."
              testidPrefix="cancelled"
              icon={XCircleLucide}
            />
          )}
          {tab === "scorecards" && <ScorecardsPanel />}
          {tab === "invoicing" && <InvoicingPanel />}
          {tab === "preferences" && <AppPreferencesPanel />}
          {tab === "bookings" && <BookingsPanel />}
          {tab === "import" && <ImportTrips />}
          {tab === "kaizen" && <KaizenPanel />}
          {tab === "admin-users" && <AdminUsersPanel />}
          {tab === "comm-policy" && <CommPolicyPanel />}
          {tab === "main-line" && <MainLinePanel />}
          {tab === "monitoring" && <VAMonitoringPanel />}
          {tab === "facilities" && <AdminFacilities />}
          {tab === "recurring" && <RecurringSeries />}
          {tab === "meetings" && <MeetingsPanel />}
          {tab === "drivers" && <DriversPanel />}
          {tab === "partners" && <PartnersPanel />}
          {tab === "messages" && <MessagesPanel />}
          {tab === "ova" && <OvaPanel />}
          {tab === "delivery" && <DeliveryLogPanel />}
          {tab === "settings" && <EmailSettingsPanel />}
          {tab === "integrations" && <IntegrationsPanel />}
          </Suspense>
        </div>
      </div>
      <CredentialingIncidentsModal open={incidentsOpen} onClose={() => setIncidentsOpen(false)}/>
    </main>
  );
}

/* --------------- App Preferences --------------- */
function AppPreferencesPanel() {
  const [showLegacy, setShowLegacy] = useAppPref("showLegacyGroup");
  return (
    <div className="max-w-2xl">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-slate_navy">Preferences</h2>
        <p className="text-sm text-slate-500">Per-browser display options. These settings live on this device.</p>
      </div>
      <div className="bg-white border border-[#E2E0D8] rounded-xl p-5 mb-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="font-semibold text-slate_navy mb-1">Show &ldquo;Legacy (archive)&rdquo; sidebar section</div>
            <p className="text-xs text-slate-500 leading-relaxed">
              When on, the sidebar shows the legacy Dispatch and Bookings tabs for lookup of pre-migration data.
              Turned off by default to keep the dispatcher view clean.
            </p>
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="pref-legacy-toggle">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={!!showLegacy}
              onChange={(e) => setShowLegacy(e.target.checked)}
            />
            <span className="relative w-11 h-6 rounded-full bg-slate-200 peer-checked:bg-forest transition-colors">
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${showLegacy ? "translate-x-5" : ""}`}/>
            </span>
            <span className="text-xs text-slate-600 w-8">{showLegacy ? "On" : "Off"}</span>
          </label>
        </div>
      </div>
    </div>
  );
}

/* --------------- Bookings --------------- */
function BookingsPanel() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [serviceArea, setServiceArea] = useState("all");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/bookings");
      setBookings(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const counts = useMemo(() => {
    const c = { all: bookings.length, pending: 0, confirmed: 0, completed: 0, cancelled: 0 };
    bookings.forEach((b) => (c[b.status] = (c[b.status] || 0) + 1));
    return c;
  }, [bookings]);

  const serviceAreas = useMemo(() => {
    const set = new Set();
    bookings.forEach((b) => {
      const sa = (b.service_area || "").trim();
      if (sa) set.add(sa);
    });
    return Array.from(set).sort();
  }, [bookings]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return bookings.filter((b) => {
      if (filter !== "all" && b.status !== filter) return false;
      if (dateFrom && b.pickup_date < dateFrom) return false;
      if (dateTo && b.pickup_date > dateTo) return false;
      if (serviceArea !== "all") {
        if (serviceArea === "__none__") {
          if ((b.service_area || "").trim()) return false;
        } else if ((b.service_area || "").trim() !== serviceArea) return false;
      }
      if (q) {
        const blob = [
          b.facility_name,
          b.contact_name,
          b.contact_email,
          b.contact_phone,
          b.patient_name,
          b.pickup_address,
          b.dropoff_address,
          b.trip_type,
          b.notes,
          b.service_area,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!blob.includes(q)) return false;
      }
      return true;
    });
  }, [bookings, filter, search, dateFrom, dateTo, serviceArea]);

  const exportCsv = () => {
    if (visible.length === 0) {
      toast.error("No rows to export with current filters.");
      return;
    }
    const headers = [
      "id",
      "created_at",
      "status",
      "facility_name",
      "contact_name",
      "contact_phone",
      "contact_email",
      "patient_name",
      "mobility_type",
      "trip_type",
      "pickup_date",
      "pickup_time",
      "pickup_address",
      "dropoff_address",
      "return_trip",
      "notes",
    ];
    const escape = (v) => {
      if (v === null || v === undefined) return "";
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const rows = visible.map((b) => headers.map((h) => escape(b[h])).join(","));
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `medexova-bookings-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${visible.length} booking${visible.length === 1 ? "" : "s"}.`);
  };

  const clearFilters = () => {
    setFilter("all");
    setSearch("");
    setDateFrom("");
    setDateTo("");
    setServiceArea("all");
  };

  const updateStatus = async (id, status) => {
    try {
      await apiClient.patch(`/bookings/${id}/status`, { status });
      toast.success(`Marked ${status}`);
      load();
      if (selected?.id === id) setSelected({ ...selected, status });
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Update failed");
    }
  };

  const removeBooking = async (id) => {
    if (!window.confirm("Delete this booking permanently?")) return;
    try {
      await apiClient.delete(`/bookings/${id}`);
      toast.success("Deleted");
      setSelected(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  const filtersActive = filter !== "all" || search || dateFrom || dateTo || serviceArea !== "all";

  return (
    <div data-testid="bookings-panel">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#E2E0D8] border border-[#E2E0D8] rounded-md overflow-hidden">
        {[
          { k: "Total", v: counts.all, color: "text-slate_navy" },
          { k: "Pending", v: counts.pending || 0, color: "text-terracotta" },
          { k: "Confirmed", v: counts.confirmed || 0, color: "text-forest" },
          { k: "Completed", v: counts.completed || 0, color: "text-slate-500" },
        ].map((s) => (
          <div key={s.k} className="bg-white p-5">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500">{s.k}</div>
            <div className={`font-heading text-3xl font-bold mt-1 ${s.color}`}>{s.v}</div>
          </div>
        ))}
      </div>

      <div className="mt-8 flex items-center justify-between gap-3 flex-wrap">
        <h1 className="font-heading text-2xl font-semibold text-slate_navy">Trip requests</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={exportCsv}
            data-testid="export-csv-btn"
            className="inline-flex items-center gap-2 rounded-md bg-forest hover:bg-forest-hover text-white px-3 py-2 text-sm font-medium"
          >
            <DownloadSimple size={14} /> Export CSV
          </button>
          <button
            onClick={load}
            className="rounded-md border border-[#E2E0D8] bg-white px-3 py-2 text-sm hover:bg-slate-50"
            data-testid="refresh-bookings-btn"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* NEMTPlatform-style top filter chip strip (date range + service area) */}
      <div className="mt-4 -mx-1 px-1 overflow-x-auto" data-testid="bookings-chip-strip">
        <div className="inline-flex items-center gap-2 whitespace-nowrap pb-1">
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="rounded-full border border-[#E2E0D8] bg-white px-3 py-1.5 text-xs font-medium text-slate_navy"
            data-testid="chip-date-from"
          />
          <span className="text-slate-400 text-xs">→</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="rounded-full border border-[#E2E0D8] bg-white px-3 py-1.5 text-xs font-medium text-slate_navy"
            data-testid="chip-date-to"
          />
          <span className="mx-2 h-5 w-px bg-slate-200" />
          <button
            onClick={() => setServiceArea("all")}
            data-testid="chip-area-all"
            className={`rounded-full border px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition ${
              serviceArea === "all"
                ? "bg-brandblue text-white border-brandblue"
                : "bg-white text-slate-600 border-[#E2E0D8] hover:border-brandblue/50"
            }`}
          >
            All areas
          </button>
          {serviceAreas.map((sa) => (
            <button
              key={sa}
              onClick={() => setServiceArea(sa)}
              data-testid={`chip-area-${sa}`}
              className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                serviceArea === sa
                  ? "bg-brandblue text-white border-brandblue"
                  : "bg-white text-slate-600 border-[#E2E0D8] hover:border-brandblue/50"
              }`}
            >
              {sa}
            </button>
          ))}
          {!serviceAreas.length && (
            <span className="text-[11px] text-slate-400 italic px-2">no service areas tagged yet</span>
          )}
        </div>
      </div>

      {/* Filter bar */}
      <div className="mt-4 rounded-lg border border-[#E2E0D8] bg-white p-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search facility, patient, address…"
            className="w-full rounded-md border border-[#E2E0D8] bg-white pl-9 pr-3 py-2 text-sm"
            data-testid="booking-search-input"
          />
        </div>
        <div className="flex items-center gap-2">
          <Funnel size={14} className="text-slate-500 shrink-0" />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            data-testid="booking-filter"
            className="w-full rounded-md border border-[#E2E0D8] bg-white px-3 py-2 text-sm"
          >
            <option value="all">All statuses ({counts.all})</option>
            <option value="pending">Pending ({counts.pending || 0})</option>
            <option value="confirmed">Confirmed ({counts.confirmed || 0})</option>
            <option value="completed">Completed ({counts.completed || 0})</option>
            <option value="cancelled">Cancelled ({counts.cancelled || 0})</option>
          </select>
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 mb-1">Pickup from</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="w-full rounded-md border border-[#E2E0D8] bg-white px-3 py-1.5 text-sm"
            data-testid="booking-date-from"
          />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 mb-1">Pickup to</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="w-full rounded-md border border-[#E2E0D8] bg-white px-3 py-1.5 text-sm"
            data-testid="booking-date-to"
          />
        </div>
        <div className="sm:col-span-2 lg:col-span-4 flex items-center justify-between text-xs text-slate-500">
          <span data-testid="booking-result-count">
            {visible.length} of {bookings.length} {bookings.length === 1 ? "booking" : "bookings"}
            {filtersActive ? " · filters active" : ""}
          </span>
          {filtersActive && (
            <button
              onClick={clearFilters}
              className="text-forest hover:text-forest-hover font-medium"
              data-testid="clear-filters-btn"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      <div className="mt-4 rounded-lg border border-[#E2E0D8] bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr className="text-xs uppercase tracking-[0.18em] text-slate-500">
                <th className="px-4 py-3 font-semibold">Facility</th>
                <th className="px-4 py-3 font-semibold">Patient</th>
                <th className="px-4 py-3 font-semibold">Trip</th>
                <th className="px-4 py-3 font-semibold">When</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">Loading…</td></tr>
              ) : visible.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-16 text-center">
                    <div className="font-heading text-lg text-slate_navy">
                      {bookings.length === 0 ? "No trip requests yet" : "No bookings match the current filters"}
                    </div>
                    <p className="mt-2 text-sm text-slate-500">
                      {bookings.length === 0
                        ? "When a facility submits the booking form, it will appear here."
                        : "Try clearing filters or widening the date range."}
                    </p>
                  </td>
                </tr>
              ) : (
                visible.map((b) => (
                  <tr
                    key={b.id}
                    onClick={() => setSelected(b)}
                    className="border-t border-[#E2E0D8] hover:bg-slate-50/60 cursor-pointer transition-colors"
                    data-testid={`booking-row-${b.id}`}
                  >
                    <td className="px-4 py-4">
                      <div className="font-medium text-slate_navy">{b.facility_name}</div>
                      <div className="text-xs text-slate-500">{b.contact_name} · {b.contact_phone}</div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-slate_navy">{b.patient_name}</div>
                      <div className="text-xs text-slate-500 capitalize">{b.mobility_type}</div>
                    </td>
                    <td className="px-4 py-4 text-slate_navy capitalize">{b.trip_type.replace(/_/g, " ")}</td>
                    <td className="px-4 py-4 text-slate_navy">{b.pickup_date} · {b.pickup_time}</td>
                    <td className="px-4 py-4">
                      <span className={`inline-block border rounded-full px-2.5 py-0.5 text-[11px] uppercase tracking-[0.15em] font-bold ${STATUS_STYLES[b.status] || ""}`}>
                        {b.status}
                      </span>
                      {b.ova_trip_id ? (
                        <div className="mt-1 text-[10px] uppercase tracking-[0.18em] font-semibold text-ova" title={b.ova_trip_id}>
                          OVA · {String(b.ova_trip_id).slice(-8)}
                        </div>
                      ) : b.ova_error ? (
                        <div className="mt-1 text-[10px] uppercase tracking-[0.18em] font-semibold text-red-600" title={b.ova_error}>
                          OVA · failed
                        </div>
                      ) : null}
                    </td>
                    <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => setSelected(b)} className="inline-flex items-center gap-1 text-slate_navy hover:text-forest" data-testid={`view-booking-${b.id}`}>
                          <Eye size={16} />
                        </button>
                        <StatusMenu current={b.status} onChange={(s) => updateStatus(b.id, s)} bookingId={b.id} />
                        <button onClick={() => removeBooking(b.id)} className="text-slate-400 hover:text-red-600" data-testid={`delete-booking-${b.id}`}>
                          <Trash size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <BookingDetailDrawer
          booking={selected}
          onClose={() => setSelected(null)}
          onStatus={(s) => updateStatus(selected.id, s)}
          onChanged={(updated) => {
            setSelected((prev) => (prev ? { ...prev, ...updated } : prev));
            load();
          }}
        />
      )}
    </div>
  );
}

function StatusMenu({ current, onChange, bookingId }) {
  const [open, setOpen] = useState(false);
  const options = ["pending", "confirmed", "completed", "cancelled"];
  return (
    <div className="relative">
      <button onClick={() => setOpen((v) => !v)} data-testid={`status-menu-${bookingId}`} className="inline-flex items-center gap-1 rounded-md border border-[#E2E0D8] bg-white px-2.5 py-1 text-xs text-slate_navy hover:bg-slate-50">
        Set status <CaretDown size={12} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 w-40 z-40 rounded-md border border-[#E2E0D8] bg-white shadow-lg overflow-hidden">
            {options.map((o) => (
              <button
                key={o}
                onClick={() => { onChange(o); setOpen(false); }}
                data-testid={`status-option-${bookingId}-${o}`}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 capitalize ${o === current ? "text-forest font-semibold" : "text-slate_navy"}`}
              >
                {o}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function BookingDetailDrawer({ booking, onClose, onStatus, onChanged }) {
  const b = booking;
  const [forwarding, setForwarding] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [emailingPdf, setEmailingPdf] = useState(false);
  const [timeline, setTimeline] = useState([]);
  const [copied, setCopied] = useState(false);
  const publicUrl = `${window.location.origin}/t/${b.id}`;

  // Authenticated PDF download (Bearer token can't go through <a href>, so use blob)
  const downloadMedicaidPdf = async (e, bookingId) => {
    e.preventDefault();
    try {
      const res = await apiClient.get(`/bookings/${bookingId}/medicaid-tripsheet.pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `medexova-tripsheet-${bookingId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Failed to generate trip sheet — check console");
    }
  };

  const emailTripsheet = async () => {
    setEmailingPdf(true);
    try {
      const { data } = await apiClient.post(`/bookings/${b.id}/email-tripsheet`);
      if (data?.emailed_to) {
        toast.success(`Trip sheet emailed to ${data.emailed_to}`);
        onChanged?.();
      } else {
        toast.warning("No billing email on file — set billing_contact_email on the facility.");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Email failed");
    } finally {
      setEmailingPdf(false);
    }
  };

  const viewPodPhoto = async (e, publicUrl) => {
    e.preventDefault();
    // Endpoint is JWT-protected — fetch as blob and open in new tab
    try {
      const path = publicUrl.replace(/^\/api/, "");
      const res = await apiClient.get(path, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      window.open(url, "_blank", "noopener,noreferrer");
      // Best-effort revoke after 60s
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("Failed to open POD photo");
    }
  };

  const loadTimeline = async () => {
    try {
      const { data } = await apiClient.get(`/bookings/${b.id}/timeline`);
      setTimeline(data.events || []);
    } catch (err) { /* ignore */ }
  };

  useEffect(() => {
    loadTimeline();
    // Auto-poll OVA when trip is active
    const active = b.ova_trip_id && ["confirmed", "in_progress"].includes(b.status);
    if (!active) return;
    const t = setInterval(async () => {
      try {
        await apiClient.post(`/bookings/${b.id}/refresh-ova`);
        loadTimeline();
      } catch (err) { /* ignore */ }
    }, 25000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [b.id, b.status, b.ova_trip_id]);

  const refreshNow = async () => {
    if (!b.ova_trip_id) return;
    setRefreshing(true);
    try {
      const { data } = await apiClient.post(`/bookings/${b.id}/refresh-ova`);
      toast.success(`OVA · ${data.ova_status || "updated"}`);
      onChanged?.({ ...b, ova_status: data.ova_status, driver_name: data.trip?.driver_name, driver_phone: data.trip?.driver_phone });
      loadTimeline();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      toast.success("Tracking link copied");
      setTimeout(() => setCopied(false), 1400);
    } catch {
      toast.error("Copy failed — select and copy manually");
    }
  };

  const forwardOva = async () => {
    setForwarding(true);
    try {
      const { data } = await apiClient.post(`/bookings/${b.id}/forward-ova`);
      if (data.ova_trip_id) toast.success(`Sent to OVA · ${data.ova_trip_id.slice(-8)}`);
      else if (data.ova_error) toast.error(`OVA: ${data.ova_error}`);
      onChanged?.(data);
      loadTimeline();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Forward failed");
    } finally {
      setForwarding(false);
    }
  };

  const cancelOva = async () => {
    if (!window.confirm("Cancel this trip on OVA Dispatch?")) return;
    setCancelling(true);
    try {
      const { data } = await apiClient.post(`/bookings/${b.id}/cancel-ova`);
      if (data.cancelled) {
        toast.success("Trip cancelled on OVA");
        onChanged?.({ ...b, status: "cancelled", ova_status: "cancelled" });
      } else toast.error("OVA rejected cancel request");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Cancel failed");
    } finally {
      setCancelling(false);
    }
  };

  const [dispatchSwitching, setDispatchSwitching] = useState(false);
  const setDispatchMode = async (mode) => {
    if (dispatchSwitching || (b.dispatch_mode || "astat") === mode) return;
    if (mode === "ova_autonomy" && !window.confirm(
      "Hand this trip over to OVA Autonomy?\n\n" +
      "• OVA will assign the driver from its own pool.\n" +
      "• MedexOVA will no longer be able to assign a local driver.\n" +
      "• If not yet forwarded, MedexOVA will forward to OVA now."
    )) return;
    setDispatchSwitching(true);
    try {
      const { data } = await apiClient.post(`/bookings/${b.id}/dispatch-mode`, { mode });
      toast.success(mode === "ova_autonomy" ? "Trip transferred to OVA autonomy" : "Trip back under MedexOVA dispatch");
      onChanged?.(data);
      loadTimeline();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not change dispatch mode");
    } finally {
      setDispatchSwitching(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="booking-detail-drawer">
      <div className="absolute inset-0 bg-slate_navy/40" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white h-full overflow-y-auto shadow-xl">
        <div className="p-6 border-b border-[#E2E0D8] flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-brandblue">Booking · {b.id.slice(0, 8)}</div>
            <h3 className="font-heading text-2xl font-semibold text-slate_navy mt-1">{b.facility_name}</h3>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate_navy text-2xl leading-none" data-testid="close-drawer-btn">×</button>
        </div>
        <div className="p-6 space-y-6 text-sm">
          <DetailSection title="Trip">
            <Row label="Type">{b.trip_type.replace(/_/g, " ")}</Row>
            <Row label="Mobility">{b.mobility_type}</Row>
            <Row label="Date / time">{b.pickup_date} · {b.pickup_time}</Row>
            <Row label="Return trip">{b.return_trip ? "Yes" : "No"}</Row>
            <Row label="Pickup">{b.pickup_address}</Row>
            <Row label="Drop-off">{b.dropoff_address}</Row>
            {b.notes && <Row label="Notes">{b.notes}</Row>}
          </DetailSection>
          <DetailSection title="Patient">
            <Row label="Name">{b.patient_name}</Row>
            {b.patient_email && <Row label="Email">{b.patient_email}</Row>}
            {b.patient_phone && <Row label="Phone">{b.patient_phone}</Row>}
          </DetailSection>
          {/* Sprint 5: Dispatch ownership toggle */}
          <DetailSection title="Dispatch ownership">
            <div className="flex items-center gap-2 mb-3" data-testid="dispatch-mode-toggle">
              <button
                onClick={() => setDispatchMode("astat")}
                disabled={dispatchSwitching}
                data-testid="dispatch-mode-astat-btn"
                className={`flex-1 px-3 py-2 rounded-md text-xs font-semibold border transition-colors ${
                  (b.dispatch_mode || "astat") === "astat"
                    ? "bg-forest text-white border-forest"
                    : "bg-white text-slate_navy border-[#E2E0D8] hover:bg-slate-50"
                }`}
              >
                MedexOVA (managed)
              </button>
              <button
                onClick={() => setDispatchMode("ova_autonomy")}
                disabled={dispatchSwitching}
                data-testid="dispatch-mode-ova-btn"
                className={`flex-1 px-3 py-2 rounded-md text-xs font-semibold border transition-colors ${
                  b.dispatch_mode === "ova_autonomy"
                    ? "bg-ova text-white border-ova"
                    : "bg-white text-slate_navy border-[#E2E0D8] hover:bg-slate-50"
                }`}
              >
                OVA autonomy
              </button>
            </div>
            <p className="text-xs text-slate-500 leading-relaxed" data-testid="dispatch-mode-helptext">
              {b.dispatch_mode === "ova_autonomy"
                ? "OVA owns this trip — OVA picks the driver from its pool. MedexOVA is read-only on driver fields."
                : "MedexOVA owns this trip — assign a local driver below. Toggle to OVA Autonomy to hand off."}
            </p>
            {b.dispatch_mode_changed_by && (
              <p className="mt-2 text-[11px] text-slate-400">
                Last changed by {b.dispatch_mode_changed_by} · {b.dispatch_mode_changed_at?.slice(0, 16)?.replace("T", " ")}
              </p>
            )}
          </DetailSection>
          <DetailSection title="Facility contact">
            <Row label="Facility">{b.facility_name}</Row>
            <Row label="Contact">{b.contact_name}</Row>
            <Row label="Phone">{b.contact_phone}</Row>
            <Row label="Email">{b.contact_email}</Row>
          </DetailSection>
          {(b.pickup_lat || b.dropoff_lat || b.driver_lat) && (
            <DetailSection title="Live map">
              <TripMap
                theme="light"
                height={220}
                pickup={b.pickup_lat ? { lat: b.pickup_lat, lng: b.pickup_lng, label: b.pickup_address } : null}
                dropoff={b.dropoff_lat ? { lat: b.dropoff_lat, lng: b.dropoff_lng, label: b.dropoff_address } : null}
                driver={b.driver_lat ? { lat: b.driver_lat, lng: b.driver_lng } : null}
              />
            </DetailSection>
          )}

          <DetailSection title="Public tracking link">
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={publicUrl}
                onFocus={(e) => e.target.select()}
                className="flex-1 rounded-md border border-[#E2E0D8] bg-slate-50 px-3 py-2 text-xs font-mono text-slate_navy"
                data-testid="drawer-tracking-url"
                style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
              />
              <button onClick={copyLink} data-testid="drawer-copy-link-btn" className="inline-flex items-center gap-1 rounded-md border border-brandblue/30 bg-brandblue-soft hover:bg-brandblue hover:text-white text-brandblue-deep px-3 py-2 text-xs font-semibold transition-colors">
                {copied ? <CheckCircle size={12} weight="fill" /> : <CopySimple size={12} />} Copy
              </button>
              <a href={publicUrl} target="_blank" rel="noreferrer" data-testid="drawer-open-link-btn" className="inline-flex items-center rounded-md border border-[#E2E0D8] bg-white hover:bg-slate-50 text-slate_navy px-3 py-2 text-xs font-semibold">
                <LinkSimple size={12} />
              </a>
            </div>
            <p className="text-[11px] text-slate-500 mt-2">Share this with the facility coordinator — patient initials only, no PHI.</p>
          </DetailSection>

          {(b.driver_name || b.driver_phone) && (
            <DetailSection title="Driver">
              {b.driver_name && <Row label="Name">{b.driver_name}</Row>}
              {b.driver_phone && (
                <Row label="Phone">
                  <a href={`tel:${b.driver_phone}`} className="text-brandblue inline-flex items-center gap-1">
                    <Phone size={12} /> {b.driver_phone}
                  </a>
                </Row>
              )}
            </DetailSection>
          )}

          <DetailSection title="OVA Dispatch">
            {b.ova_trip_id ? (
              <>
                <Row label="Trip ID">
                  <span className="font-mono text-xs">{b.ova_trip_id}</span>
                </Row>
                {b.ova_status && <Row label="OVA status">{b.ova_status}</Row>}
                {b.ova_forwarded_at && <Row label="Sent at">{new Date(b.ova_forwarded_at).toLocaleString()}</Row>}
                <div className="pt-2 flex flex-wrap gap-2">
                  <button onClick={refreshNow} disabled={refreshing} data-testid="drawer-ova-refresh-btn" className="inline-flex items-center gap-2 rounded-md border border-ova/30 bg-ova/10 hover:bg-ova hover:text-white disabled:opacity-60 text-ova-hover px-3 py-1.5 text-xs font-semibold transition-colors">
                    <ArrowsClockwise size={12} /> {refreshing ? "Refreshing…" : "Refresh from OVA"}
                  </button>
                  <button onClick={cancelOva} disabled={cancelling} data-testid="drawer-ova-cancel-btn" className="inline-flex items-center gap-2 rounded-md border border-red-200 bg-white text-red-600 hover:bg-red-50 disabled:opacity-60 px-3 py-1.5 text-xs font-semibold">
                    {cancelling ? "Cancelling…" : "Cancel on OVA"}
                  </button>
                  <button onClick={forwardOva} disabled={forwarding} data-testid="drawer-ova-resend-btn" className="inline-flex items-center gap-2 rounded-md border border-[#E2E0D8] bg-white hover:bg-slate-50 disabled:opacity-60 px-3 py-1.5 text-xs font-semibold">
                    {forwarding ? "Sending…" : "Resend to OVA"}
                  </button>
                </div>
              </>
            ) : (
              <>
                {b.ova_error ? (
                  <Row label="Last error">
                    <span className="text-red-600">{b.ova_error}</span>
                  </Row>
                ) : (
                  <p className="text-xs text-slate-500">Not yet sent to OVA.</p>
                )}
                <div className="pt-2">
                  <button onClick={forwardOva} disabled={forwarding} data-testid="drawer-ova-forward-btn" className="inline-flex items-center gap-2 rounded-md bg-forest hover:bg-forest-hover disabled:opacity-60 text-white px-3 py-1.5 text-xs font-semibold">
                    {forwarding ? "Sending…" : "Send to OVA Dispatch"} <ArrowRight weight="bold" size={12} />
                  </button>
                </div>
              </>
            )}
          </DetailSection>
          <DetailSection title="Documents">
            <div className="flex flex-wrap gap-2">
              <a
                href="#"
                data-testid="detail-tripsheet-pdf"
                onClick={(e) => downloadMedicaidPdf(e, b.id)}
                className="inline-flex items-center gap-2 rounded-md border border-brandblue/30 bg-brandblue/5 hover:bg-brandblue hover:text-white text-brandblue-deep px-3 py-1.5 text-xs font-semibold transition-colors"
              >
                <DownloadSimple size={12} /> Medicaid trip-sheet PDF
              </a>
              <button
                type="button"
                onClick={emailTripsheet}
                disabled={emailingPdf}
                data-testid="detail-email-tripsheet"
                className="inline-flex items-center gap-2 rounded-md border border-forest/30 bg-forest/5 hover:bg-forest hover:text-white text-forest-hover disabled:opacity-60 px-3 py-1.5 text-xs font-semibold transition-colors"
              >
                <EnvelopeSimple size={12} /> {emailingPdf ? "Sending…" : "Email tripsheet to billing"}
              </button>
            </div>
            {b.medicaid_pdf_emailed_at && (
              <p className="mt-2 text-[11px] text-slate-500" data-testid="detail-tripsheet-emailed-info">
                Last sent to <b className="text-slate_navy">{b.medicaid_pdf_emailed_to}</b> at {new Date(b.medicaid_pdf_emailed_at).toLocaleString()}
              </p>
            )}
            {(b.pod_photo_public_url || b.pod_signature_text) && (
              <div className="mt-3 rounded-lg border border-[#E2E0D8] bg-slate-50 p-3" data-testid="detail-pod-block">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-brandblue mb-2">Proof of delivery</div>
                {b.pod_photo_public_url && (
                  <button
                    type="button"
                    onClick={(e) => viewPodPhoto(e, b.pod_photo_public_url)}
                    className="block rounded-md overflow-hidden border border-[#E2E0D8] hover:border-brandblue/50 transition"
                    data-testid="detail-pod-thumb"
                  >
                    <span className="text-xs text-brandblue hover:underline inline-flex items-center gap-1 px-3 py-2">
                      <Eye size={12} /> View POD photo
                    </span>
                  </button>
                )}
                {b.pod_signature_text && (
                  <div className="mt-2 text-xs text-slate_navy">
                    <span className="text-slate-500">Signed:</span> <i>"{b.pod_signature_text}"</i>
                  </div>
                )}
                {b.pod_uploaded_at && (
                  <div className="mt-1 text-[11px] text-slate-400">Uploaded {new Date(b.pod_uploaded_at).toLocaleString()}</div>
                )}
              </div>
            )}
            {b.quoted_price !== undefined && b.quoted_price !== null && (
              <div className="mt-3 rounded-md border border-[#E2E0D8] bg-slate-50 px-3 py-2 text-xs">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-brandblue">Quoted fare</div>
                <div className="mt-1 font-heading text-xl font-black text-ink">${Number(b.quoted_price).toFixed(2)}</div>
                {b.quote_breakdown && (
                  <div className="mt-1 text-[11px] text-slate-500">
                    {b.quote_breakdown.miles ? `${b.quote_breakdown.miles} mi · ` : ""}
                    base ${b.quote_breakdown.base_fee_usd ?? "—"} +
                    mileage ${b.quote_breakdown.mileage_cost_usd ?? "—"}
                    {b.quote_breakdown.mobility_surcharge_usd ? ` + surcharge $${b.quote_breakdown.mobility_surcharge_usd}` : ""}
                  </div>
                )}
              </div>
            )}
          </DetailSection>
          <DetailSection title="Status">
            <div className="flex flex-wrap gap-2">
              {["pending", "confirmed", "completed", "cancelled"].map((s) => (
                <button
                  key={s}
                  onClick={() => onStatus(s)}
                  data-testid={`detail-status-${s}`}
                  className={`rounded-md border px-3 py-1.5 text-xs uppercase tracking-[0.15em] font-bold capitalize ${
                    b.status === s
                      ? s === "confirmed" || s === "completed"
                        ? "bg-forest text-white border-forest"
                        : s === "cancelled"
                        ? "bg-red-600 text-white border-red-600"
                        : "bg-terracotta text-white border-terracotta"
                      : "bg-white text-slate_navy border-[#E2E0D8] hover:bg-slate-50"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </DetailSection>
          <DetailSection title="Event timeline">
            {timeline.length === 0 ? (
              <p className="text-sm text-slate-500">No events yet.</p>
            ) : (
              <ol className="relative border-l-2 border-brandblue/20 ml-2 space-y-3">
                {timeline.map((ev) => (
                  <li key={ev.id} className="ml-4 relative">
                    <span className="absolute -left-[11px] top-1 h-3 w-3 rounded-full bg-brandblue ring-4 ring-white"></span>
                    <div className="text-sm text-slate_navy font-medium">{EVENT_LABEL[ev.event] || ev.event}</div>
                    <div className="text-[11px] text-slate-400 font-mono mt-0.5" style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>
                      {new Date(ev.at).toLocaleString()} · {ev.source}
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </DetailSection>
          <p className="text-xs text-slate-500">Submitted {b.created_at}</p>
        </div>
      </div>
    </div>
  );
}

function DetailSection({ title, children }) {
  return (
    <div>
      <h4 className="text-xs uppercase tracking-[0.2em] font-bold text-brandblue mb-3">{title}</h4>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
function Row({ label, children }) {
  return (
    <div className="grid grid-cols-[110px_1fr] gap-3 text-sm">
      <div className="text-slate-500">{label}</div>
      <div className="text-slate_navy break-words">{children}</div>
    </div>
  );
}

/* --------------- Messages --------------- */
function MessagesPanel() {
  const [msgs, setMsgs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/contact-messages");
      setMsgs(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  return (
    <div data-testid="messages-panel">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-semibold text-slate_navy">Contact messages</h1>
        <button onClick={load} className="rounded-md border border-[#E2E0D8] bg-white px-3 py-2 text-sm hover:bg-slate-50">Refresh</button>
      </div>
      <div className="mt-4 grid md:grid-cols-2 gap-4">
        {loading ? (
          <div className="text-slate-500">Loading…</div>
        ) : msgs.length === 0 ? (
          <div className="md:col-span-2 rounded-lg border border-[#E2E0D8] bg-white p-10 text-center">
            <ChatCircleText size={32} className="mx-auto text-slate-400" />
            <div className="mt-3 font-heading text-lg text-slate_navy">No messages yet</div>
            <p className="mt-1 text-sm text-slate-500">Submissions from the contact form will appear here.</p>
          </div>
        ) : (
          msgs.map((m) => (
            <div key={m.id} className="rounded-lg border border-[#E2E0D8] bg-white p-5" data-testid={`message-${m.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-semibold text-slate_navy">{m.name}</div>
                  <div className="text-xs text-slate-500">{m.email}{m.phone ? ` · ${m.phone}` : ""}</div>
                </div>
                <div className="text-xs text-slate-400">{new Date(m.created_at).toLocaleString()}</div>
              </div>
              {m.subject && <div className="mt-3 text-sm font-medium text-brandblue">{m.subject}</div>}
              <p className="mt-2 text-sm text-slate-700 whitespace-pre-wrap">{m.message}</p>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

/* --------------- Email Settings --------------- */
function EmailSettingsPanel() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testingSms, setTestingSms] = useState(false);
  const [form, setForm] = useState({
    brevo_api_key: "",
    sender_email: "dispatch@medexova.com",
    sender_name: "MedexOVA Medical Transportation",
    recipient_email: "info@medexova.com",
    enabled: true,
    sms_enabled: false,
    sms_sender: "MedexOVA",
    sms_recipient: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/settings/email");
      setSettings(data);
      setForm((f) => ({
        ...f,
        brevo_api_key: "", // don't pre-fill key for safety
        sender_email: data.sender_email,
        sender_name: data.sender_name,
        recipient_email: data.recipient_email,
        enabled: data.enabled,
        sms_enabled: data.sms_enabled || false,
        sms_sender: data.sms_sender || "MedexOVA",
        sms_recipient: data.sms_recipient || "",
      }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await apiClient.put("/settings/email", form);
      setSettings(data);
      setForm((f) => ({ ...f, brevo_api_key: "" }));
      toast.success("Notification settings saved.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const testEmail = async () => {
    setTesting(true);
    try {
      const { data } = await apiClient.post("/settings/email/test");
      if (data.sent) toast.success("Test email sent. Check your inbox.");
      else toast.error("Could not send — verify API key and try again.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  const testSms = async () => {
    setTestingSms(true);
    try {
      const { data } = await apiClient.post("/settings/sms/test");
      if (data.sent) toast.success("Test SMS sent.");
      else toast.error("SMS not sent — check API key, sender, recipient and that SMS is enabled.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Test failed");
    } finally {
      setTestingSms(false);
    }
  };

  const input =
    "w-full rounded-md border border-[#E2E0D8] bg-white px-4 py-3 text-sm text-slate_navy placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-forest/20 focus:border-forest transition";

  return (
    <div data-testid="email-settings-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-slate_navy">Email notifications</h1>
          <p className="mt-1 text-sm text-slate-600 max-w-xl">
            Configure the Brevo API key used to email booking and contact submissions. Add the key
            whenever it's available — bookings are saved either way.
          </p>
        </div>
        <div className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs uppercase tracking-[0.2em] font-bold border-[#E2E0D8] bg-white">
          {settings?.configured ? (
            <><CheckCircle size={14} className="text-forest" /> <span className="text-forest">API key set</span></>
          ) : (
            <><XCircle size={14} className="text-terracotta" /> <span className="text-terracotta">Not configured</span></>
          )}
        </div>
      </div>

      <form onSubmit={save} className="mt-8 max-w-2xl space-y-6" data-testid="email-settings-form">
        <div>
          <label className="block text-xs uppercase tracking-[0.2em] font-bold text-slate_navy mb-2">Brevo API key</label>
          <input
            type="password"
            value={form.brevo_api_key}
            onChange={(e) => setForm({ ...form, brevo_api_key: e.target.value })}
            placeholder={settings?.api_key_hint ? `Current: ${settings.api_key_hint}` : "xkeysib-…"}
            className={input}
            data-testid="brevo-api-key-input"
          />
          <p className="mt-2 text-xs text-slate-500">
            Get one from{" "}
            <a className="underline" href="https://app.brevo.com/settings/keys/api" target="_blank" rel="noreferrer">
              Brevo → SMTP & API → API keys
            </a>
            . Leave blank to keep the current key.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] font-bold text-slate_navy mb-2">Sender name</label>
            <input
              value={form.sender_name}
              onChange={(e) => setForm({ ...form, sender_name: e.target.value })}
              className={input}
              data-testid="sender-name-input"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] font-bold text-slate_navy mb-2">Sender email</label>
            <input
              type="email"
              value={form.sender_email}
              onChange={(e) => setForm({ ...form, sender_email: e.target.value })}
              className={input}
              data-testid="sender-email-input"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs uppercase tracking-[0.2em] font-bold text-slate_navy mb-2">Recipient email (dispatch inbox)</label>
            <input
              type="email"
              value={form.recipient_email}
              onChange={(e) => setForm({ ...form, recipient_email: e.target.value })}
              className={input}
              data-testid="recipient-email-input"
            />
          </div>
        </div>

        <label className="flex items-center gap-3 text-sm text-slate_navy">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
            className="h-4 w-4 rounded border-[#E2E0D8] text-forest focus:ring-forest"
            data-testid="notifications-enabled-checkbox"
          />
          Enable outbound email notifications
        </label>

        {/* ---- SMS (Brevo transactional SMS) ---- */}
        <div className="pt-6 border-t border-[#E2E0D8]">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h3 className="font-heading text-base font-semibold text-slate_navy">SMS alerts (Brevo)</h3>
              <p className="mt-1 text-xs text-slate-500 max-w-md">
                Get paged on your phone the moment a trip request lands — same key, different channel.
              </p>
            </div>
            <label className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-bold text-slate_navy">
              <input
                type="checkbox"
                checked={form.sms_enabled}
                onChange={(e) => setForm({ ...form, sms_enabled: e.target.checked })}
                className="h-4 w-4 rounded border-[#E2E0D8] text-brandblue focus:ring-brandblue"
                data-testid="sms-enabled-checkbox"
              />
              SMS enabled
            </label>
          </div>
          <div className="mt-4 grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] font-bold text-slate_navy mb-2">Sender ID (2–11 chars)</label>
              <input
                value={form.sms_sender}
                onChange={(e) => setForm({ ...form, sms_sender: e.target.value.slice(0, 11) })}
                className={input}
                placeholder="MedexOVA"
                data-testid="sms-sender-input"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] font-bold text-slate_navy mb-2">Recipient phone (E.164)</label>
              <input
                value={form.sms_recipient}
                onChange={(e) => setForm({ ...form, sms_recipient: e.target.value })}
                onBlur={() => setForm((f) => ({ ...f, sms_recipient: autoPrefixUS(f.sms_recipient) }))}
                className={input}
                placeholder="+19409230878"
                data-testid="sms-recipient-input"
              />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 pt-4 border-t border-[#E2E0D8] flex-wrap">
          <button
            type="submit"
            disabled={saving}
            data-testid="save-email-settings-btn"
            className="inline-flex items-center gap-2 rounded-md bg-forest hover:bg-forest-hover disabled:opacity-60 text-white px-5 py-2.5 text-sm font-semibold"
          >
            {saving ? "Saving…" : "Save settings"} <ArrowRight weight="bold" size={14} />
          </button>
          <button
            type="button"
            onClick={testEmail}
            disabled={!settings?.configured || testing}
            data-testid="test-email-btn"
            className="inline-flex items-center gap-2 rounded-md border border-[#E2E0D8] bg-white hover:bg-slate-50 disabled:opacity-60 text-slate_navy px-5 py-2.5 text-sm font-semibold"
          >
            <PaperPlaneTilt size={14} /> {testing ? "Sending…" : "Send test email"}
          </button>
          <button
            type="button"
            onClick={testSms}
            disabled={!settings?.configured || !form.sms_enabled || !form.sms_recipient || testingSms}
            data-testid="test-sms-btn"
            className="inline-flex items-center gap-2 rounded-md border border-brandblue/30 bg-brandblue-soft hover:bg-brandblue hover:text-white disabled:opacity-60 disabled:hover:bg-brandblue-soft disabled:hover:text-brandblue-deep text-brandblue-deep px-5 py-2.5 text-sm font-semibold transition-colors"
          >
            <PaperPlaneTilt size={14} /> {testingSms ? "Sending…" : "Send test SMS"}
          </button>
        </div>
      </form>
    </div>
  );
}

/* --------------- OVA Dispatch Panel --------------- */
function OvaPanel() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [form, setForm] = useState({
    api_key: "",
    base_url: "https://ovadispatch.com/api/atms/partner",
    webhook_url: "",
    partner_id: "",
    enabled: true,
    auto_forward: true,
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/settings/ova");
      setSettings(data);
      setForm({
        api_key: "",
        base_url: data.base_url || "https://ovadispatch.com/api/atms/partner",
        webhook_url: data.webhook_url || "",
        partner_id: data.partner_id || "",
        enabled: data.enabled,
        auto_forward: data.auto_forward,
      });
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await apiClient.put("/settings/ova", form);
      setSettings(data);
      setForm((f) => ({ ...f, api_key: "" }));
      toast.success("OVA settings saved.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const registerWebhook = async () => {
    setRegistering(true);
    try {
      const { data } = await apiClient.post("/settings/ova/register-webhook");
      if (data.registered) toast.success("Webhook registered with OVA.");
      else toast.error(`OVA rejected webhook registration (HTTP ${data.status_code}).`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Register failed");
    } finally {
      setRegistering(false);
    }
  };

  const input =
    "w-full rounded-md border border-[#E2E0D8] bg-white px-4 py-3 text-sm text-slate_navy placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-forest/20 focus:border-forest transition";

  return (
    <div data-testid="ova-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-slate_navy inline-flex items-center gap-2">
            <PlugsConnected size={22} weight="duotone" className="text-ova" /> OVA Dispatch integration
          </h1>
          <p className="mt-1 text-sm text-slate-600 max-w-xl">
            MedexOVA bookings are auto-forwarded to OVA Dispatch as trips. OVA pushes driver, pickup,
            and completion updates back to this site via webhook — booking status updates automatically.
          </p>
        </div>
        <div className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs uppercase tracking-[0.2em] font-bold border-[#E2E0D8] bg-white" data-testid="ova-config-badge">
          {settings?.configured ? (
            <><CheckCircle size={14} className="text-ova" /> <span className="text-ova">API key set</span></>
          ) : (
            <><XCircle size={14} className="text-ova-hover" /> <span className="text-ova-hover">Not configured</span></>
          )}
        </div>
      </div>

      <form onSubmit={save} className="mt-8 max-w-2xl space-y-6" data-testid="ova-settings-form">
        <div>
          <label className="block text-xs uppercase tracking-[0.2em] font-bold text-slate_navy mb-2">OVA Partner API key</label>
          <input
            type="password"
            value={form.api_key}
            onChange={(e) => setForm({ ...form, api_key: e.target.value })}
            placeholder={settings?.api_key_hint ? `Current: ${settings.api_key_hint}` : "ova_partner_…"}
            className={input}
            data-testid="ova-api-key-input"
          />
          <p className="mt-2 text-xs text-slate-500">Leave blank to keep the current key.</p>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] font-bold text-slate_navy mb-2">Base URL</label>
            <input
              value={form.base_url}
              onChange={(e) => setForm({ ...form, base_url: e.target.value })}
              className={input}
              data-testid="ova-base-url-input"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] font-bold text-slate_navy mb-2">Partner ID</label>
            <input
              value={form.partner_id}
              onChange={(e) => setForm({ ...form, partner_id: e.target.value })}
              placeholder="PARTNER_…"
              className={input}
              data-testid="ova-partner-id-input"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs uppercase tracking-[0.2em] font-bold text-slate_navy mb-2">Webhook URL (OVA → MedexOVA)</label>
            <div className="flex gap-2">
              <input
                value={form.webhook_url}
                onChange={(e) => setForm({ ...form, webhook_url: e.target.value })}
                className={input}
                placeholder="https://…/api/webhooks/ova"
                data-testid="ova-webhook-url-input"
              />
              <button
                type="button"
                onClick={registerWebhook}
                disabled={!settings?.configured || !form.webhook_url || registering}
                data-testid="ova-register-webhook-btn"
                className="inline-flex shrink-0 items-center gap-2 rounded-md border border-[#E2E0D8] bg-white hover:bg-slate-50 disabled:opacity-60 text-slate_navy px-4 py-3 text-sm font-semibold"
              >
                <Broadcast size={14} /> {registering ? "…" : "Register"}
              </button>
            </div>
            <p className="mt-2 text-xs text-slate-500">
              Paste your public URL + <code>/api/webhooks/ova</code>. Register sends OVA a <code>PUT /webhook</code> call.
            </p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <label className="flex items-center gap-3 text-sm text-slate_navy rounded-md border border-[#E2E0D8] bg-white px-4 py-3">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
              className="h-4 w-4 rounded border-[#E2E0D8] text-forest focus:ring-forest"
              data-testid="ova-enabled-checkbox"
            />
            Integration enabled
          </label>
          <label className="flex items-center gap-3 text-sm text-slate_navy rounded-md border border-[#E2E0D8] bg-white px-4 py-3">
            <input
              type="checkbox"
              checked={form.auto_forward}
              onChange={(e) => setForm({ ...form, auto_forward: e.target.checked })}
              className="h-4 w-4 rounded border-[#E2E0D8] text-forest focus:ring-forest"
              data-testid="ova-auto-forward-checkbox"
            />
            Auto-forward new bookings to OVA
          </label>
        </div>

        <div className="flex items-center gap-3 pt-4 border-t border-[#E2E0D8]">
          <button
            type="submit"
            disabled={saving}
            data-testid="save-ova-settings-btn"
            className="inline-flex items-center gap-2 rounded-md bg-ova hover:bg-ova-hover disabled:opacity-60 text-white px-5 py-2.5 text-sm font-semibold"
          >
            {saving ? "Saving…" : "Save settings"} <ArrowRight weight="bold" size={14} />
          </button>
          <a
            href="https://ovadispatch.com"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-forest"
          >
            OVA Dispatch <ArrowSquareOut size={14} />
          </a>
        </div>
      </form>

      {loading ? null : (
        <div className="mt-10 rounded-lg border border-[#E2E0D8] bg-white p-6 max-w-2xl">
          <h3 className="font-heading text-lg font-semibold text-slate_navy">How it works</h3>
          <ol className="mt-3 space-y-2 text-sm text-slate-600 list-decimal list-inside">
            <li>Facilities submit a trip request through the MedexOVA booking portal.</li>
            <li>MedexOVA immediately forwards the trip to OVA Dispatch using your API key.</li>
            <li>OVA assigns a driver and pushes updates (<code>driver_assigned</code>, <code>in_progress</code>, <code>completed</code>, <code>cancelled</code>) back to MedexOVA via webhook.</li>
            <li>Booking status on this dashboard updates automatically as events arrive.</li>
          </ol>
        </div>
      )}
    </div>
  );
}


/* --------------- Drivers Panel --------------- */
const VEHICLE_TYPES = ["sedan", "suv", "wheelchair", "stretcher"];

function DriversPanel() {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", vehicle_type: "sedan", license_state: "TX", notes: "", photo_url: "", service_areas: [] });
  const [saving, setSaving] = useState(false);
  const [areaInput, setAreaInput] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  // ?filter=below_verified — Trust Ops → Identity verification "?" deep-links here
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = searchParams.get("filter") || "";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/drivers");
      setDrivers(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiClient.post("/drivers", form);
      toast.success(`Invite sent to ${form.email}`);
      setForm({ name: "", email: "", phone: "", vehicle_type: "sedan", license_state: "TX", notes: "", photo_url: "", service_areas: [] });
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Invite failed");
    } finally {
      setSaving(false);
    }
  };

  const resendInvite = async (id) => {
    try {
      await apiClient.post(`/drivers/${id}/resend-invite`);
      toast.success("Invite re-sent");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Resend failed");
    }
  };

  const testNotify = async (id) => {
    if (!window.confirm(
      "Send a test EMAIL to this driver?\n\n" +
      "This verifies the driver's email is correct.\n" +
      "(SMS is currently disabled — Brevo drops driver SMS silently.)"
    )) return;
    try {
      const { data } = await apiClient.post(`/drivers/${id}/test-notify`, {
        channels: ["email"],
      });
      const r = data.results || {};
      if (r.email?.ok) {
        toast.success(`Test email sent to ${r.email.to}`);
      } else {
        toast.error(`Email send failed: ${r.email?.error || "unknown"}`);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Test-notify failed");
    }
  };

  const setStatus = async (id, status) => {
    try {
      await apiClient.patch(`/drivers/${id}`, { status });
      toast.success(`Driver ${status}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this driver permanently? Only allowed if they have no trips on record. Use Archive instead to hide them without losing history.")) return;
    try {
      await apiClient.delete(`/drivers/${id}`);
      toast.success("Driver deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  const input = "w-full rounded-md border border-[#E2E0D8] bg-white px-3 py-2 text-sm text-slate_navy placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brandblue/25 focus:border-brandblue transition";

  return (
    <div data-testid="drivers-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-slate_navy inline-flex items-center gap-2">
            <Users size={22} weight="duotone" className="text-brandblue" /> Driver roster
          </h1>
          <p className="mt-1 text-sm text-slate-600 max-w-xl">
            Invite drivers into the MedexOVA × OVA network. They receive a Brevo email with their OVA Driver-app signup link
            (partner code + invite code). Once they complete signup, mark them <b>active</b>.
          </p>
        </div>
        <button
          onClick={() => setShowForm((v) => !v)}
          data-testid="toggle-invite-form-btn"
          className="inline-flex items-center gap-2 rounded-md bg-brandblue-ink hover:bg-brandblue-deep text-white px-4 py-2 text-sm font-semibold"
        >
          <UserPlus size={14} /> Invite driver
        </button>
      </div>
      {showForm && (
        <form onSubmit={submit} className="mt-6 rounded-lg border border-[#E2E0D8] bg-white p-6 grid sm:grid-cols-2 gap-4" data-testid="driver-invite-form">
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] font-bold text-slate_navy mb-2">Full name</label>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} data-testid="driver-name-input" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] font-bold text-slate_navy mb-2">Email</label>
            <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={input} data-testid="driver-email-input" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] font-bold text-slate_navy mb-2">Phone</label>
            <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                   onBlur={() => setForm((f) => ({ ...f, phone: autoPrefixUS(f.phone) }))}
                   className={input} placeholder="+19725551212" data-testid="driver-phone-input" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] font-bold text-slate_navy mb-2">Vehicle type</label>
            <select value={form.vehicle_type} onChange={(e) => setForm({ ...form, vehicle_type: e.target.value })} className={input} data-testid="driver-vehicle-select">
              {VEHICLE_TYPES.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] font-bold text-slate_navy mb-2">License state</label>
            <input value={form.license_state} onChange={(e) => setForm({ ...form, license_state: e.target.value.toUpperCase().slice(0, 2) })} className={input} data-testid="driver-license-input" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] font-bold text-slate_navy mb-2">Notes</label>
            <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={input} placeholder="WC-ramp cert, overnight availability, etc." data-testid="driver-notes-input" />
          </div>
          {/* Photo URL — used by dispatch console avatar marker */}
          <div className="sm:col-span-2">
            <label className="block text-xs uppercase tracking-[0.2em] font-bold text-slate_navy mb-2">Driver photo</label>
            <div className="flex items-center gap-3">
              {form.photo_url ? (
                <img
                  src={form.photo_url}
                  alt="Driver"
                  className="h-14 w-14 rounded-full object-cover border-2 border-brandblue/30"
                  data-testid="driver-photo-preview"
                />
              ) : (
                <div className="h-14 w-14 rounded-full bg-slate-100 border border-dashed border-slate-300 inline-flex items-center justify-center text-slate-400 text-[10px] uppercase">
                  no photo
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                data-testid="driver-photo-file"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  if (file.size > 2_500_000) {
                    toast.error("Photo too large (max 2.5 MB)");
                    return;
                  }
                  // Base64-inline (drivers table doesn't have a file storage endpoint yet —
                  // a data: URL works in <img> and in the dispatch SVG avatar)
                  const reader = new FileReader();
                  reader.onload = () => setForm((f) => ({ ...f, photo_url: reader.result }));
                  reader.readAsDataURL(file);
                }}
                className="text-xs"
              />
              {form.photo_url && (
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, photo_url: "" }))}
                  className="text-xs text-slate-500 hover:text-red-600 underline"
                >
                  Remove
                </button>
              )}
            </div>
          </div>
          {/* Service areas — chip input */}
          <div className="sm:col-span-2">
            <label className="block text-xs uppercase tracking-[0.2em] font-bold text-slate_navy mb-2">Service areas</label>
            <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-[#E2E0D8] bg-white px-3 py-2 min-h-[42px]">
              {form.service_areas.map((sa, i) => (
                <span
                  key={`${sa}-${i}`}
                  data-testid={`driver-area-chip-${i}`}
                  className="inline-flex items-center gap-1 rounded-full bg-brandblue/10 text-brandblue-deep border border-brandblue/30 px-2.5 py-0.5 text-[11px] font-semibold"
                >
                  {sa}
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, service_areas: f.service_areas.filter((_, j) => j !== i) }))}
                    className="ml-0.5 text-brandblue hover:text-red-600"
                    aria-label="Remove"
                  >
                    ×
                  </button>
                </span>
              ))}
              <input
                value={areaInput}
                onChange={(e) => setAreaInput(e.target.value)}
                onKeyDown={(e) => {
                  if ((e.key === "Enter" || e.key === ",") && areaInput.trim()) {
                    e.preventDefault();
                    const v = areaInput.trim().slice(0, 60);
                    if (!form.service_areas.includes(v)) {
                      setForm((f) => ({ ...f, service_areas: [...f.service_areas, v] }));
                    }
                    setAreaInput("");
                  } else if (e.key === "Backspace" && !areaInput && form.service_areas.length) {
                    setForm((f) => ({ ...f, service_areas: f.service_areas.slice(0, -1) }));
                  }
                }}
                placeholder={form.service_areas.length ? "Add another…" : "e.g. Dallas, Plano, Frisco (press Enter)"}
                className="flex-1 min-w-[140px] outline-none text-sm bg-transparent"
                data-testid="driver-area-input"
              />
            </div>
            <p className="mt-1 text-[11px] text-slate-500">Used by the auto-assign engine + Bookings filter chips.</p>
          </div>
          <div className="sm:col-span-2 flex items-center justify-end gap-3 pt-2 border-t border-[#E2E0D8]">
            <button type="button" onClick={() => setShowForm(false)} className="text-sm text-slate-500 hover:text-slate_navy">Cancel</button>
            <button type="submit" disabled={saving} data-testid="submit-invite-btn" className="inline-flex items-center gap-2 rounded-md bg-brandblue-ink hover:bg-brandblue-deep disabled:opacity-60 text-white px-5 py-2.5 text-sm font-semibold">
              {saving ? "Sending…" : "Send invite"} <ArrowRight weight="bold" size={14} />
            </button>
          </div>
        </form>
      )}

      <div className="mt-6 rounded-lg border border-[#E2E0D8] bg-white overflow-hidden">
        {/* Filter strip — controlled by ?filter= URL param so deep-links from
            Trust Ops land here with the correct view already applied. */}
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[#E2E0D8] bg-slate-50 text-xs">
          <span className="uppercase tracking-[0.18em] text-slate-500 font-bold">Filter</span>
          <button
            type="button"
            data-testid="driver-filter-all"
            onClick={() => setSearchParams({})}
            className={"px-3 py-1 rounded-full font-semibold " + (!filter ? "bg-forest text-white" : "bg-white text-slate-600 border border-[#E2E0D8]")}
          >All ({drivers.length})</button>
          <button
            type="button"
            data-testid="driver-filter-below-verified"
            onClick={() => setSearchParams({ filter: "below_verified" })}
            className={"px-3 py-1 rounded-full font-semibold " + (filter === "below_verified" ? "bg-amber-600 text-white" : "bg-white text-slate-600 border border-[#E2E0D8]")}
          >Not MedexOVA Verified ({drivers.filter(d => (d.credential_score ?? 0) < 80).length})</button>
          <button
            type="button"
            data-testid="driver-filter-no-face"
            onClick={() => setSearchParams({ filter: "no_face" })}
            className={"px-3 py-1 rounded-full font-semibold " + (filter === "no_face" ? "bg-amber-600 text-white" : "bg-white text-slate-600 border border-[#E2E0D8]")}
          >No face enrollment ({drivers.filter(d => !d.face_enrolled).length})</button>
          <button
            type="button"
            data-testid="driver-toggle-show-archived"
            onClick={() => setShowArchived((s) => !s)}
            className={"px-3 py-1 rounded-full font-semibold " + (showArchived ? "bg-slate-700 text-white" : "bg-white text-slate-600 border border-[#E2E0D8]")}
          >{showArchived ? "Hide" : "Show"} archived ({drivers.filter(d => d.status === "archived").length})</button>
          {filter && (
            <span className="ml-auto text-[11px] text-slate-500 italic">
              deep-linked from Trust Ops → clear to show all
            </span>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr className="text-xs uppercase tracking-[0.18em] text-slate-500">
                <th className="px-4 py-3 font-semibold">Driver</th>
                <th className="px-4 py-3 font-semibold">Contact</th>
                <th className="px-4 py-3 font-semibold">Vehicle</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Invited</th>
                <th className="px-4 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const filtered = drivers.filter(d => {
                  if (filter === "below_verified") return (d.credential_score ?? 0) < 80;
                  if (filter === "no_face") return !d.face_enrolled;
                  if (!showArchived && d.status === "archived") return false;
                  return true;
                });
                if (loading) {
                  return <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">Loading…</td></tr>;
                }
                if (filtered.length === 0) {
                  return (
                    <tr>
                      <td colSpan={6} className="px-4 py-16 text-center">
                        <div className="font-heading text-lg text-slate_navy">
                          {filter ? "No drivers match this filter" : "No drivers yet"}
                        </div>
                        <p className="mt-2 text-sm text-slate-500">
                          {filter ? "Try 'All' above." : <>Click <b>Invite driver</b> to onboard your first one.</>}
                        </p>
                      </td>
                    </tr>
                  );
                }
                return filtered.map((d) => (
                <tr key={d.id} className={`border-t border-[#E2E0D8] hover:bg-slate-50/60 ${d.status === "archived" ? "opacity-50" : ""}`} data-testid={`driver-row-${d.id}`}>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      {d.photo_url ? (
                        <img src={d.photo_url} alt={d.name} className="h-9 w-9 rounded-full object-cover border border-brandblue/30" />
                      ) : (
                        <div className="h-9 w-9 rounded-full bg-brandblue/10 text-brandblue-deep inline-flex items-center justify-center text-xs font-bold">
                          {(d.name || "").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <div className="font-medium text-slate_navy">{d.name}</div>
                        {d.license_state && <div className="text-xs text-slate-500">License · {d.license_state}</div>}
                        {d.service_areas?.length > 0 && (
                          <div className="mt-0.5 flex flex-wrap gap-1">
                            {d.service_areas.slice(0, 3).map((sa) => (
                              <span key={sa} className="rounded-full bg-slate-100 text-slate-600 px-1.5 py-0 text-[10px]">{sa}</span>
                            ))}
                            {d.service_areas.length > 3 && <span className="text-[10px] text-slate-400">+{d.service_areas.length - 3}</span>}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="text-slate_navy">{d.email}</div>
                    <div className="text-xs text-slate-500">{d.phone}</div>
                  </td>
                  <td className="px-4 py-4 capitalize text-slate_navy">{d.vehicle_type}</td>
                  <td className="px-4 py-4">
                    <span className={`inline-block border rounded-full px-2.5 py-0.5 text-[11px] uppercase tracking-[0.15em] font-bold ${
                      d.status === "active" ? "bg-forest/10 text-forest border-forest/20"
                        : d.status === "invited" ? "bg-brandblue-soft text-brandblue-deep border-brandblue/20"
                        : d.status === "archived" ? "bg-slate-100 text-slate-500 border-dashed border-slate-300"
                        : "bg-slate-100 text-slate-600 border-slate-200"}`}>
                      {d.status}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-xs text-slate-500">{d.last_invited_at ? new Date(d.last_invited_at).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-end gap-2">
                      {d.pin && (
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(`${window.location.origin}/driver/${d.pin}`);
                            toast.success("Driver link copied");
                          }}
                          data-testid={`copy-driver-pin-${d.id}`}
                          className="inline-flex items-center gap-1 rounded-md border border-ova/30 bg-ova/10 hover:bg-ova hover:text-white text-ova px-2.5 py-1 text-[11px] font-semibold font-mono"
                          title={`Portal PIN: ${d.pin}`}
                          style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
                        >
                          <LinkSimple size={12} /> {d.pin}
                        </button>
                      )}
                      <button onClick={() => resendInvite(d.id)} data-testid={`resend-invite-${d.id}`} className="inline-flex items-center gap-1 rounded-md border border-brandblue/30 bg-brandblue-soft hover:bg-brandblue hover:text-white text-brandblue-deep px-2.5 py-1 text-[11px] font-semibold">
                        <PaperPlaneTilt size={12} /> Resend
                      </button>
                      <button onClick={() => testNotify(d.id)} data-testid={`test-notify-${d.id}`} title="Send test email + SMS + call to verify driver contact" className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-amber-50 hover:bg-amber-500 hover:text-white text-amber-800 px-2.5 py-1 text-[11px] font-semibold">
                        Test notify
                      </button>
                      {d.status !== "active" ? (
                        <button onClick={() => setStatus(d.id, "active")} data-testid={`activate-${d.id}`} className="inline-flex items-center gap-1 rounded-md border border-forest/30 bg-forest-soft hover:bg-forest hover:text-white text-forest px-2.5 py-1 text-[11px] font-semibold">
                          <CheckCircle size={12} /> Activate
                        </button>
                      ) : (
                        <button onClick={() => setStatus(d.id, "inactive")} data-testid={`deactivate-${d.id}`} className="inline-flex items-center gap-1 rounded-md border border-[#E2E0D8] bg-white hover:bg-slate-50 text-slate_navy px-2.5 py-1 text-[11px] font-semibold">
                          Pause
                        </button>
                      )}
                      {d.status === "archived" ? (
                        <button onClick={() => setStatus(d.id, "active")} data-testid={`unarchive-driver-${d.id}`} className="inline-flex items-center gap-1 rounded-md border border-forest text-forest px-2.5 py-1 text-[11px] font-semibold">
                          Restore
                        </button>
                      ) : (
                        <button onClick={() => setStatus(d.id, "archived")} data-testid={`archive-driver-${d.id}`} className="inline-flex items-center gap-1 rounded-md border border-[#E2E0D8] bg-white hover:bg-slate-50 text-slate_navy px-2.5 py-1 text-[11px] font-semibold">
                          Archive
                        </button>
                      )}
                      <button onClick={() => remove(d.id)} className="text-slate-400 hover:text-red-600" data-testid={`delete-driver-${d.id}`}>
                        <Trash size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ));
              })()}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* --------------- Delivery Log --------------- */
function DeliveryLogPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [channel, setChannel] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const params = channel === "all" ? "" : `?channel=${channel}`;
      const { data } = await apiClient.get(`/delivery-log${params}`);
      setRows(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [channel]);

  const counts = { total: rows.length, sent: rows.filter(r => r.status === "sent").length, failed: rows.filter(r => r.status === "failed").length, skipped: rows.filter(r => r.status === "skipped").length };

  return (
    <div data-testid="delivery-log-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-slate_navy inline-flex items-center gap-2">
            <Receipt size={22} weight="duotone" className="text-brandblue" /> Delivery log
          </h1>
          <p className="mt-1 text-sm text-slate-600 max-w-xl">
            Every outbound email and SMS — with Brevo message IDs, skips, and failures. Read-only audit trail.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={channel} onChange={(e) => setChannel(e.target.value)} data-testid="delivery-channel-filter" className="rounded-md border border-[#E2E0D8] bg-white px-3 py-2 text-sm">
            <option value="all">All channels ({counts.total})</option>
            <option value="email">Email</option>
            <option value="sms">SMS</option>
          </select>
          <button onClick={load} className="rounded-md border border-[#E2E0D8] bg-white px-3 py-2 text-sm hover:bg-slate-50">Refresh</button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-px bg-[#E2E0D8] border border-[#E2E0D8] rounded-md overflow-hidden">
        {[
          { k: "Total", v: counts.total, color: "text-slate_navy" },
          { k: "Sent", v: counts.sent, color: "text-forest" },
          { k: "Failed", v: counts.failed, color: "text-red-600" },
          { k: "Skipped", v: counts.skipped, color: "text-slate-500" },
        ].map((s) => (
          <div key={s.k} className="bg-white p-5">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500">{s.k}</div>
            <div className={`font-heading text-3xl font-bold mt-1 ${s.color}`}>{s.v}</div>
          </div>
        ))}
      </div>

      <div className="mt-4 rounded-lg border border-[#E2E0D8] bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr className="text-xs uppercase tracking-[0.18em] text-slate-500">
                <th className="px-4 py-3 font-semibold">When</th>
                <th className="px-4 py-3 font-semibold">Channel</th>
                <th className="px-4 py-3 font-semibold">To</th>
                <th className="px-4 py-3 font-semibold">Subject / message</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Message ID / error</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-16 text-center text-slate-500">No delivery records.</td></tr>
              ) : rows.map((r) => (
                <tr key={r.id} className="border-t border-[#E2E0D8]" data-testid={`delivery-row-${r.id}`}>
                  <td className="px-4 py-3 text-xs text-slate-500 font-mono whitespace-nowrap" style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>
                    {new Date(r.sent_at).toLocaleString()}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block rounded-md px-2 py-0.5 text-[11px] uppercase tracking-[0.15em] font-bold ${r.channel === "email" ? "bg-brandblue-soft text-brandblue-deep" : "bg-ova/10 text-ova-hover"}`}>
                      {r.channel}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate_navy">{r.to}</td>
                  <td className="px-4 py-3 text-slate-700 max-w-[300px] truncate" title={r.subject}>{r.subject}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] uppercase tracking-[0.15em] font-bold ${
                      r.status === "sent" ? "bg-forest/10 text-forest"
                        : r.status === "failed" ? "bg-red-50 text-red-700"
                        : "bg-slate-100 text-slate-600"}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500 font-mono truncate max-w-[240px]" title={r.message_id || r.error || ""} style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>
                    {r.message_id || r.error || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}



// ---------------- Partners panel ----------------
function PartnersPanel() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    apiClient.get("/partners/leads")
      .then(({ data }) => setLeads(data))
      .catch(() => setLeads([]))
      .finally(() => setLoading(false));
  }, []);
  return (
    <div className="space-y-4" data-testid="partners-panel">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-xl font-bold text-ink">Partner leads</h2>
        <Link to="/partners" target="_blank" className="text-xs text-brandblue hover:text-brandblue-hover font-semibold inline-flex items-center gap-1">
          Open public page <ArrowSquareOut size={12} />
        </Link>
      </div>
      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : leads.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
          No leads yet. Share the <Link to="/partners" className="text-brandblue font-semibold">/partners</Link> page with facility decision-makers.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-[0.18em] text-slate-600">
              <tr>
                <th className="px-4 py-3 text-left">Organization</th>
                <th className="px-4 py-3 text-left">Type</th>
                <th className="px-4 py-3 text-left">Contact</th>
                <th className="px-4 py-3 text-left">Volume</th>
                <th className="px-4 py-3 text-left">Received</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`partner-lead-${l.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-ink">{l.organization}</div>
                    {l.message && <div className="text-xs text-slate-500 mt-0.5 max-w-sm truncate">{l.message}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs uppercase tracking-[0.15em] font-semibold text-brandblue">{l.facility_type}</td>
                  <td className="px-4 py-3">
                    <div>{l.contact_name}</div>
                    <div className="text-xs text-slate-500">
                      <a href={`mailto:${l.email}`} className="hover:text-brandblue">{l.email}</a> · {l.phone}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm">{l.monthly_trip_volume || "—"}</td>
                  <td className="px-4 py-3 text-xs text-slate-500 font-mono" style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>
                    {new Date(l.created_at).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


/* --------------- Integrations Panel --------------- */
function IntegrationsPanel() {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [drafts, setDrafts] = React.useState({});
  const [savingKey, setSavingKey] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/admin/integrations");
      setItems(data.integrations || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load integrations");
    } finally { setLoading(false); }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const save = async (service) => {
    setSavingKey(service);
    try {
      const values = drafts[service] || {};
      await apiClient.put(`/admin/integrations/${service}`, { values });
      toast.success(`${service} saved`);
      setDrafts((d) => ({ ...d, [service]: {} }));
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    } finally { setSavingKey(null); }
  };

  if (loading) return <div className="p-6 text-slate-500">Loading integrations…</div>;
  return (
    <div className="p-6 space-y-6 max-w-3xl" data-testid="integrations-panel">
      <div>
        <h1 className="text-2xl font-heading font-bold text-slate_navy">Integrations</h1>
        <p className="text-sm text-slate-500 mt-1">
          Plug in third-party service credentials. Values shown are masked; leave a field blank to keep the existing value.
        </p>
      </div>
      {items.map((it) => (
        <div key={it.service} className="rounded-2xl bg-white border border-[#E2E0D8] p-5 space-y-3 shadow-sm" data-testid={`integration-${it.service}`}>
          <div className="flex items-center justify-between">
            <div>
              <div className="font-bold text-slate_navy">{it.label}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                <span className={`inline-block px-2 py-0.5 rounded-full font-bold text-[10px] uppercase tracking-wider ${it.configured ? "bg-green-100 text-green-800" : "bg-slate-100 text-slate-600"}`}>
                  {it.configured ? "✓ Configured" : "Not configured"}
                </span>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {it.fields.map((f) => {
              const isSecret = ["service_account_json", "client_secret", "api_key", "auth_token", "webhook_secret", "secret_key"].includes(f);
              const currentValue = it.values?.[f];
              const isSet = it.values?.[`${f}_set`] || (!isSecret && currentValue);
              const draft = drafts[it.service]?.[f] ?? "";
              return (
                <div key={f} className={f === "service_account_json" ? "md:col-span-2" : ""}>
                  <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{f}</label>
                  {f === "service_account_json" ? (
                    <textarea
                      value={draft}
                      onChange={(e) => setDrafts((d) => ({ ...d, [it.service]: { ...d[it.service], [f]: e.target.value } }))}
                      rows={4}
                      data-testid={`integration-${it.service}-${f}`}
                      className="w-full font-mono text-[11px] border border-[#E2E0D8] rounded-md py-2 px-3 mt-1"
                      placeholder={isSet ? `Current: ${currentValue || "•••••"} — paste new JSON to replace` : "Paste service account JSON here"}
                    />
                  ) : (
                    <input
                      type={isSecret ? "password" : "text"}
                      value={draft}
                      onChange={(e) => setDrafts((d) => ({ ...d, [it.service]: { ...d[it.service], [f]: e.target.value } }))}
                      data-testid={`integration-${it.service}-${f}`}
                      className="w-full border border-[#E2E0D8] rounded-md py-2 px-3 mt-1"
                      placeholder={isSet ? (typeof currentValue === "string" ? currentValue : "•••••") + " — paste new to replace" : ""}
                    />
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex justify-end">
            <button
              onClick={() => save(it.service)}
              disabled={savingKey === it.service || !drafts[it.service] || Object.keys(drafts[it.service] || {}).length === 0}
              data-testid={`integration-save-${it.service}`}
              className="px-4 py-2 rounded-md bg-forest text-white text-sm font-bold disabled:opacity-50"
            >
              {savingKey === it.service ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

